import React, { useCallback, useEffect, useState, useRef, Children, Fragment, Component } from 'react';
import { TypeIcon, MailIcon, PhoneIcon, HashIcon, AlignLeftIcon, ChevronDownIcon, CircleIcon, CheckSquareIcon, SquareCheckIcon, LayoutListIcon, ColumnsIcon, TableIcon, CalendarIcon, UploadIcon, LinkIcon, EyeOffIcon, HeadingIcon, FileTextIcon, MinusIcon, MoveVerticalIcon, ImageIcon, SendIcon, PlayIcon, ShieldCheckIcon, ChevronRightIcon, CopyIcon, Trash2Icon, GripVerticalIcon, XIcon, UndoIcon, RedoIcon, SettingsIcon, WandSparklesIcon, Loader2Icon, PlusIcon, PaletteIcon, TagIcon, CodeIcon, PencilIcon, ZapIcon, SwatchBookIcon, SlidersHorizontalIcon, SparklesIcon, ImagePlusIcon, SearchIcon, EyeIcon } from 'lucide-react';
import { mediaLibraryApi, MediaLibraryRecord } from '../../services/creatorApi';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { FormBlock, FormTheme, FormBlockType, FormLogicRule, DEFAULT_FORM_THEME, FORM_PALETTE, FORM_STYLE_PRESETS, StylePreset, PaletteItem, createFormBlock, labelToName } from './types';
import { generateFormHtml } from './formHtmlGenerator';
import { callGPTAPI } from '../../utils/api';
import { LogicRulesEditor } from './LogicRulesEditor';
import { COUNTRY_CODES } from './countryPhoneCodes';
import { toast } from 'sonner';
const ICONS: Record<string, React.ElementType> = {
  Type: TypeIcon,
  Mail: MailIcon,
  Phone: PhoneIcon,
  Hash: HashIcon,
  AlignLeft: AlignLeftIcon,
  ChevronDown: ChevronDownIcon,
  Circle: CircleIcon,
  CheckSquare: CheckSquareIcon,
  SquareCheck: SquareCheckIcon,
  LayoutList: LayoutListIcon,
  Table: TableIcon,
  Columns: ColumnsIcon,
  Play: PlayIcon
};
const BEAUTIFY_INSTRUCTIONS = `You are an expert CSS designer. You receive a signup form's HTML and current CSS. The user will give you a specific instruction about what to change.

RULES:
1. Return ONLY a CSS code block — do NOT change the HTML at all.
2. Keep all existing class names (.fb-form, .fb-field, .fb-label, .fb-input, .fb-button, etc.)
3. CRITICAL: You must ONLY change the specific things the user asked for. Leave ALL other styles exactly as they are in the current CSS.
4. If the user says "make the button red", ONLY change the button color. Do NOT touch fonts, backgrounds, spacing, shadows, or anything else.
5. If the user says "change the font", ONLY change font-related properties. Do NOT touch colors, spacing, shadows, or anything else.
6. Start with the EXACT current CSS and make MINIMAL targeted changes based on the user's instruction.
7. Do NOT add any explanation — ONLY the CSS code block:
\`\`\`css
(your modified CSS here)
\`\`\`
8. The CSS should be self-contained and work with the provided HTML structure.
9. If no specific instruction is given, make subtle improvements while keeping the overall look intact — do NOT redesign from scratch.`;
function parseCssFromResponse(response: string): string {
  const cssMatch = response.match(/```css\s*\n([\s\S]*?)```/i);
  if (cssMatch) return cssMatch[1].trim();
  const genericMatch = response.match(/```\s*\n([\s\S]*?)```/i);
  if (genericMatch) return genericMatch[1].trim();
  return response.trim();
}
interface FormBuilderProps {
  initialBlocks?: FormBlock[];
  initialTheme?: FormTheme;
  initialRules?: FormLogicRule[];
  onSchemaChange: (blocks: FormBlock[], theme: FormTheme, rules: FormLogicRule[]) => void;
  onHtmlChange: (html: string, css: string) => void;
  settingsSidebar?: React.ReactNode;
  hideLogicRules?: boolean;
  customPalette?: PaletteItem[];
}
/** Group consecutive half-width blocks into rows for canvas display */
interface CanvasRow {
  type: 'full' | 'half-row';
  blocks: {
    block: FormBlock;
    flatIndex: number;
  }[];
}
function groupBlocksIntoRows(blocks: FormBlock[]): CanvasRow[] {
  const rows: CanvasRow[] = [];
  let i = 0;
  while (i < blocks.length) {
    const block = blocks[i];
    if (block.width === 'half') {
      const rowBlocks: {
        block: FormBlock;
        flatIndex: number;
      }[] = [{
        block,
        flatIndex: i
      }];
      // Check if next block is also half
      if (i + 1 < blocks.length && blocks[i + 1].width === 'half') {
        rowBlocks.push({
          block: blocks[i + 1],
          flatIndex: i + 1
        });
        i += 2;
      } else {
        i += 1;
      }
      rows.push({
        type: 'half-row',
        blocks: rowBlocks
      });
    } else {
      rows.push({
        type: 'full',
        blocks: [{
          block,
          flatIndex: i
        }]
      });
      i += 1;
    }
  }
  return rows;
}
export function FormBuilder({
  initialBlocks,
  initialTheme,
  initialRules,
  onSchemaChange,
  onHtmlChange,
  settingsSidebar,
  hideLogicRules,
  customPalette
}: FormBuilderProps) {
  const [blocks, setBlocks] = useState<FormBlock[]>(initialBlocks || []);
  const [theme, setTheme] = useState<FormTheme>(initialTheme || DEFAULT_FORM_THEME);
  const [rules, setRules] = useState<FormLogicRule[]>(initialRules || []);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeRightTab, setActiveRightTab] = useState<'properties' | 'logic'>('properties');
  const [showLogicEditor, setShowLogicEditor] = useState(false);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [history, setHistory] = useState<FormBlock[][]>([initialBlocks || []]);
  const [historyIdx, setHistoryIdx] = useState(0);
  const [collapsedCats, setCollapsedCats] = useState<Set<string>>(new Set());
  const [isBeautifying, setIsBeautifying] = useState(false);
  const [beautifyPrompt, setBeautifyPrompt] = useState('');
  const [showBeautifyInput, setShowBeautifyInput] = useState(false);
  const [sectionSettingsBlock, setSectionSettingsBlock] = useState<FormBlock | null>(null);
  // Column configuration modal state
  const [columnConfigModal, setColumnConfigModal] = useState<{
    isOpen: boolean;
    blockId: string;
    currentCount: number;
  } | null>(null);
  // Track selected child block inside a column: { parentId, colIndex, childId }
  const [selectedChild, setSelectedChild] = useState<{
    parentId: string;
    colIndex: number;
    childId: string;
  } | null>(null);
  // Track drag over column: { parentId, colIndex, position }
  const [dragOverCol, setDragOverCol] = useState<{
    parentId: string;
    colIndex: number;
    position: number;
  } | null>(null);
  // Track CSS before beautify so undo can revert it
  const [preBeautifyCss, setPreBeautifyCss] = useState<string | null>(null);
  const dragItemRef = useRef<{
    type: 'palette' | 'canvas';
    blockType?: FormBlockType;
    index?: number;
  } | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const selectedBlock = (() => {
    if (!selectedId) return null;
    // Check top-level
    const top = blocks.find(b => b.id === selectedId);
    if (top) return top;
    // Search inside section children and columns
    for (const b of blocks) {
      if (b.type === 'section' && b.children) {
        const child = b.children.find(c => c.id === selectedId);
        if (child) return child;
      }
      if (b.type === 'columns' && b.columns) {
        for (const col of b.columns) {
          const child = col.find(c => c.id === selectedId);
          if (child) return child;
        }
      }
    }
    return null;
  })();
  const pushHistory = useCallback((newBlocks: FormBlock[]) => {
    setHistory(prev => {
      const trimmed = prev.slice(0, historyIdx + 1);
      return [...trimmed, newBlocks].slice(-50);
    });
    setHistoryIdx(prev => Math.min(prev + 1, 49));
  }, [historyIdx]);
  const updateBlocks = useCallback((newBlocks: FormBlock[]) => {
    setBlocks(newBlocks);
    pushHistory(newBlocks);
    const {
      html,
      css
    } = generateFormHtml(newBlocks, theme);
    onSchemaChange(newBlocks, theme, rules);
    onHtmlChange(html, css);
  }, [theme, rules, pushHistory, onSchemaChange, onHtmlChange]);
  useEffect(() => {
    // Only sync generated HTML when the builder has blocks.
    // This prevents empty builder initialization from clearing the code editor.
    if (blocks.length > 0) {
      const {
        html,
        css
      } = generateFormHtml(blocks, theme);
      onSchemaChange(blocks, theme, rules);
      onHtmlChange(html, css);
    }
  }, [theme, rules]);
  const undo = useCallback(() => {
    // If we have a beautify CSS to revert, do that first
    if (preBeautifyCss !== null) {
      const {
        html
      } = generateFormHtml(blocks, theme);
      onHtmlChange(html, preBeautifyCss);
      setPreBeautifyCss(null);
      return;
    }
    if (historyIdx > 0) {
      const newIdx = historyIdx - 1;
      setHistoryIdx(newIdx);
      const prev = history[newIdx];
      setBlocks(prev);
      const {
        html,
        css
      } = generateFormHtml(prev, theme);
      onSchemaChange(prev, theme, rules);
      onHtmlChange(html, css);
    }
  }, [historyIdx, history, theme, rules, onSchemaChange, onHtmlChange, preBeautifyCss, blocks]);
  const redo = useCallback(() => {
    if (historyIdx < history.length - 1) {
      const newIdx = historyIdx + 1;
      setHistoryIdx(newIdx);
      const next = history[newIdx];
      setBlocks(next);
      const {
        html,
        css
      } = generateFormHtml(next, theme);
      onSchemaChange(next, theme, rules);
      onHtmlChange(html, css);
    }
  }, [historyIdx, history, theme, rules, onSchemaChange, onHtmlChange]);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo();else undo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undo, redo]);
  // AI Beautify handler
  const handleBeautify = async (styleDirection?: string) => {
    if (blocks.length === 0) return;
    setIsBeautifying(true);
    try {
      const {
        html,
        css
      } = generateFormHtml(blocks, theme);
      // Save current CSS so undo can revert
      setPreBeautifyCss(css);
      const prompt = `Here is my form HTML:\n\`\`\`html\n${html}\n\`\`\`\n\nHere is the current CSS:\n\`\`\`css\n${css}\n\`\`\`\n\n${styleDirection ? `User instruction: ${styleDirection}` : 'Make subtle improvements to polish the form. Keep the overall look intact.'}`;
      const result = await callGPTAPI(BEAUTIFY_INSTRUCTIONS, prompt);
      const newCss = parseCssFromResponse(result);
      if (newCss) {
        onHtmlChange(html, newCss);
      }
    } catch (err) {
      console.error('Beautify failed:', err);
      setPreBeautifyCss(null); // Clear on failure
    } finally {
      setIsBeautifying(false);
      setShowBeautifyInput(false);
      setBeautifyPrompt('');
    }
  };
  // Drag handlers
  const handlePaletteDragStart = (type: FormBlockType) => (e: React.DragEvent) => {
    dragItemRef.current = {
      type: 'palette',
      blockType: type
    };
    e.dataTransfer.effectAllowed = 'copy';
    setIsDragging(true);
  };
  const handleCanvasDragStart = (index: number) => (e: React.DragEvent) => {
    dragItemRef.current = {
      type: 'canvas',
      index
    };
    e.dataTransfer.effectAllowed = 'move';
    setIsDragging(true);
  };
  const handleDragOver = (index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = dragItemRef.current?.type === 'palette' ? 'copy' : 'move';
    setDragOverIdx(index);
    setDragOverCol(null);
  };
  const handleDragLeave = () => {
    setDragOverIdx(null);
  };
  const handleDragEnd = () => {
    setIsDragging(false);
    setDragOverIdx(null);
    dragItemRef.current = null;
  };
  const handleBlockDragOver = (index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const dropIdx = e.clientY < midY ? index : index + 1;
    e.dataTransfer.dropEffect = dragItemRef.current?.type === 'palette' ? 'copy' : 'move';
    setDragOverIdx(dropIdx);
    setDragOverCol(null);
  };
  const handleBlockDrop = (index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const dropIdx = e.clientY < midY ? index : index + 1;
    setDragOverIdx(null);
    setIsDragging(false);
    const drag = dragItemRef.current;
    if (!drag) return;
    if (drag.type === 'palette' && drag.blockType) {
      const newBlock = createFormBlock(drag.blockType);
      const newBlocks = [...blocks];
      newBlocks.splice(dropIdx, 0, newBlock);
      updateBlocks(newBlocks);
      setSelectedId(newBlock.id);
      setSelectedChild(null);
      // Open config modal if it's a columns block
      if (newBlock.type === 'columns') {
        setColumnConfigModal({
          isOpen: true,
          blockId: newBlock.id,
          currentCount: newBlock.columnCount || 2
        });
      }
    } else if (drag.type === 'canvas' && drag.index !== undefined) {
      if (drag.index === index) {
        dragItemRef.current = null;
        return;
      }
      const newBlocks = [...blocks];
      const [moved] = newBlocks.splice(drag.index, 1);
      const adjustedIdx = drag.index < dropIdx ? dropIdx - 1 : dropIdx;
      newBlocks.splice(adjustedIdx, 0, moved);
      updateBlocks(newBlocks);
    }
    dragItemRef.current = null;
  };
  const handleDrop = (dropIndex: number) => (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverIdx(null);
    setIsDragging(false);
    const drag = dragItemRef.current;
    if (!drag) return;
    if (drag.type === 'palette' && drag.blockType) {
      const newBlock = createFormBlock(drag.blockType);
      const newBlocks = [...blocks];
      newBlocks.splice(dropIndex, 0, newBlock);
      updateBlocks(newBlocks);
      setSelectedId(newBlock.id);
      setSelectedChild(null);
      // Open config modal if it's a columns block
      if (newBlock.type === 'columns') {
        setColumnConfigModal({
          isOpen: true,
          blockId: newBlock.id,
          currentCount: newBlock.columnCount || 2
        });
      }
    } else if (drag.type === 'canvas' && drag.index !== undefined) {
      const newBlocks = [...blocks];
      const [moved] = newBlocks.splice(drag.index, 1);
      const adjustedIdx = drag.index < dropIndex ? dropIndex - 1 : dropIndex;
      newBlocks.splice(adjustedIdx, 0, moved);
      updateBlocks(newBlocks);
    }
    dragItemRef.current = null;
  };
  const handleCanvasDropEnd = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverIdx(null);
    setDragOverCol(null);
    setIsDragging(false);
    const drag = dragItemRef.current;
    if (!drag) return;
    if (drag.type === 'palette' && drag.blockType) {
      const newBlock = createFormBlock(drag.blockType);
      updateBlocks([...blocks, newBlock]);
      setSelectedId(newBlock.id);
      setSelectedChild(null);
      // Open config modal if it's a columns block
      if (newBlock.type === 'columns') {
        setColumnConfigModal({
          isOpen: true,
          blockId: newBlock.id,
          currentCount: newBlock.columnCount || 2
        });
      }
    }
    dragItemRef.current = null;
  };
  // Column drop handlers
  const handleColumnDragOver = (parentId: string, colIndex: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const drag = dragItemRef.current;
    if (!drag) return;
    // Don't allow sections, subforms, or columns inside columns
    if (drag.type === 'palette' && drag.blockType && !['section', 'subform', 'columns'].includes(drag.blockType)) {
      const parent = blocks.find(b => b.id === parentId);
      const col = parent?.columns?.[colIndex] || [];
      setDragOverCol({
        parentId,
        colIndex,
        position: col.length
      });
      setDragOverIdx(null);
      e.dataTransfer.dropEffect = 'copy';
    } else if (drag.type === 'canvas' && drag.index !== undefined) {
      const sourceBlock = blocks[drag.index];
      if (sourceBlock && !['section', 'subform', 'columns'].includes(sourceBlock.type)) {
        const parent = blocks.find(b => b.id === parentId);
        const col = parent?.columns?.[colIndex] || [];
        setDragOverCol({
          parentId,
          colIndex,
          position: col.length
        });
        setDragOverIdx(null);
        e.dataTransfer.dropEffect = 'move';
      }
    }
  };
  const handleColumnDrop = (parentId: string, colIndex: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverCol(null);
    setDragOverIdx(null);
    setIsDragging(false);
    const drag = dragItemRef.current;
    if (!drag) return;
    if (drag.type === 'palette' && drag.blockType && !['section', 'subform', 'columns'].includes(drag.blockType)) {
      const newBlock = createFormBlock(drag.blockType);
      const newBlocks = blocks.map(b => {
        if (b.id !== parentId) return b;
        const cols = (b.columns || []).map((col, i) => i === colIndex ? [...col, newBlock] : col);
        return {
          ...b,
          columns: cols
        };
      });
      updateBlocks(newBlocks);
      setSelectedId(newBlock.id);
      setSelectedChild({
        parentId,
        colIndex,
        childId: newBlock.id
      });
    } else if (drag.type === 'palette' && drag.blockType && ['section', 'subform', 'columns'].includes(drag.blockType)) {
      toast.error('Cannot nest this block inside a column', {
        description: `${drag.blockType} blocks cannot be placed inside columns.`
      });
    } else if (drag.type === 'canvas' && drag.index !== undefined) {
      const sourceBlock = blocks[drag.index];
      if (!sourceBlock || ['section', 'subform', 'columns'].includes(sourceBlock.type)) {
        if (sourceBlock) toast.error('Cannot nest this block inside a column', {
          description: `${sourceBlock.type} blocks cannot be placed inside columns.`
        });
        return;
      }
      // Remove from top-level
      const remaining = blocks.filter((_, i) => i !== drag.index);
      // Add to column
      const newBlocks = remaining.map(b => {
        if (b.id !== parentId) return b;
        const cols = (b.columns || []).map((col, i) => i === colIndex ? [...col, sourceBlock] : col);
        return {
          ...b,
          columns: cols
        };
      });
      updateBlocks(newBlocks);
      setSelectedId(sourceBlock.id);
      setSelectedChild({
        parentId,
        colIndex,
        childId: sourceBlock.id
      });
    }
    dragItemRef.current = null;
  };
  const deleteChildBlock = (parentId: string, colIndex: number, childId: string) => {
    const newBlocks = blocks.map(b => {
      if (b.id !== parentId) return b;
      const cols = (b.columns || []).map((col, i) => i === colIndex ? col.filter(cb => cb.id !== childId) : col);
      return {
        ...b,
        columns: cols
      };
    });
    updateBlocks(newBlocks);
    if (selectedId === childId) {
      setSelectedId(null);
      setSelectedChild(null);
    }
  };
  const deleteBlock = (id: string) => {
    // Check if it's a top-level block
    if (blocks.some(b => b.id === id)) {
      updateBlocks(blocks.filter(b => b.id !== id));
    } else {
      // Check inside section children and columns
      const newBlocks = blocks.map(b => {
        if (b.type === 'section' && b.children) {
          const filtered = b.children.filter(c => c.id !== id);
          if (filtered.length !== b.children.length) {
            return {
              ...b,
              children: filtered
            };
          }
        }
        if (b.type === 'columns' && b.columns) {
          const newCols = b.columns.map(col => {
            const filtered = col.filter(c => c.id !== id);
            return filtered;
          });
          return {
            ...b,
            columns: newCols
          };
        }
        return b;
      });
      updateBlocks(newBlocks);
    }
    if (selectedId === id) {
      setSelectedId(null);
      setSelectedChild(null);
    }
  };
  const duplicateBlock = (id: string) => {
    const idx = blocks.findIndex(b => b.id === id);
    if (idx === -1) return;
    const clone = {
      ...blocks[idx],
      id: Math.random().toString(36).substr(2, 9),
      name: blocks[idx].name + '_copy'
    };
    const newBlocks = [...blocks];
    newBlocks.splice(idx + 1, 0, clone);
    updateBlocks(newBlocks);
    setSelectedId(clone.id);
  };
  const updateBlock = (id: string, updates: Partial<FormBlock>) => {
    const newBlocks = blocks.map(b => {
      if (b.id === id) {
        return {
          ...b,
          ...updates
        };
      }
      // Also check section children
      if (b.type === 'section' && b.children) {
        const childIdx = b.children.findIndex(c => c.id === id);
        if (childIdx !== -1) {
          const newChildren = [...b.children];
          newChildren[childIdx] = {
            ...newChildren[childIdx],
            ...updates
          };
          return {
            ...b,
            children: newChildren
          };
        }
      }
      // Also check columns
      if (b.type === 'columns' && b.columns) {
        const newCols = b.columns.map(col => {
          const childIdx = col.findIndex(c => c.id === id);
          if (childIdx !== -1) {
            const newCol = [...col];
            newCol[childIdx] = {
              ...newCol[childIdx],
              ...updates
            };
            return newCol;
          }
          return col;
        });
        return {
          ...b,
          columns: newCols
        };
      }
      return b;
    });
    setBlocks(newBlocks);
    const {
      html,
      css
    } = generateFormHtml(newBlocks, theme);
    onSchemaChange(newBlocks, theme, rules);
    onHtmlChange(html, css);
  };
  const commitToHistory = () => pushHistory(blocks);
  const activePalette = customPalette || FORM_PALETTE;
  const categories = [...new Set(activePalette.map(p => p.category))];
  const toggleCat = (cat: string) => {
    setCollapsedCats(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);else next.add(cat);
      return next;
    });
  };
  // Group blocks into rows for canvas display
  const canvasRows = groupBlocksIntoRows(blocks);
  // Handle drop into a section block
  const handleSectionDrop = (sectionId: string) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const ref = dragItemRef.current;
    if (!ref) return;
    if (ref.type === 'palette' && ref.blockType) {
      // Don't allow nesting sections, columns, or subforms inside sections
      if (ref.blockType === 'section' || ref.blockType === 'columns' || ref.blockType === 'subform' || ref.blockType === 'submit') {
        toast.error('Cannot nest this block inside a section', {
          description: `${ref.blockType} blocks cannot be placed inside sections.`
        });
        return;
      }
      const newBlock = createFormBlock(ref.blockType);
      const newBlocks = blocks.map(b => {
        if (b.id === sectionId) {
          return {
            ...b,
            children: [...(b.children || []), newBlock]
          };
        }
        return b;
      });
      updateBlocks(newBlocks);
      setSelectedId(newBlock.id);
    } else if (ref.type === 'canvas' && ref.index !== undefined) {
      // Move an existing block into the section
      const sourceBlock = blocks[ref.index];
      if (!sourceBlock || sourceBlock.type === 'section' || sourceBlock.type === 'columns' || sourceBlock.type === 'subform' || sourceBlock.type === 'submit') return;
      if (sourceBlock.id === sectionId) return;
      // Remove from top-level
      const remaining = blocks.filter((_, i) => i !== ref.index);
      // Add to section children
      const newBlocks = remaining.map(b => {
        if (b.id === sectionId) {
          return {
            ...b,
            children: [...(b.children || []), sourceBlock]
          };
        }
        return b;
      });
      updateBlocks(newBlocks);
      setSelectedId(sourceBlock.id);
    }
    dragItemRef.current = null;
    setDragOverIdx(null);
  };
  // Remove a child from a section
  const removeChildFromSection = (sectionId: string, childId: string) => {
    const section = blocks.find(b => b.id === sectionId);
    if (!section) return;
    const child = (section.children || []).find(c => c.id === childId);
    if (!child) return;
    // Remove from section children
    const sectionIdx = blocks.findIndex(b => b.id === sectionId);
    const newBlocks = blocks.map(b => {
      if (b.id === sectionId) {
        return {
          ...b,
          children: (b.children || []).filter(c => c.id !== childId)
        };
      }
      return b;
    });
    // Insert the child after the section in the top-level list
    newBlocks.splice(sectionIdx + 1, 0, child);
    updateBlocks(newBlocks);
  };
  // Render a single block card (used for both full and half-width)
  const renderBlockCard = (block: FormBlock, flatIndex: number) => <div key={block.id} draggable onDragStart={handleCanvasDragStart(flatIndex)} onDragEnd={handleDragEnd} onDragOver={block.type === 'section' || block.type === 'columns' ? undefined : handleBlockDragOver(flatIndex)} onDrop={block.type === 'section' || block.type === 'columns' ? undefined : handleBlockDrop(flatIndex)} onClick={e => {
    e.stopPropagation();
    setSelectedId(block.id);
    setSelectedChild(null);
    setActiveRightTab('properties');
  }} className={`relative group border-2 rounded-lg transition-all ${selectedId === block.id && !selectedChild ? 'border-blue-500 shadow-sm bg-blue-50/10' : 'border-transparent hover:border-gray-300 bg-white'}`} data-id="element-642">
      {/* Block Actions */}
      <div className={`absolute -right-3 -top-3 flex items-center gap-1 z-10 transition-opacity ${selectedId === block.id && !selectedChild ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`} data-id="element-643">
        {block.type === 'section' && <button onClick={e => {
        e.stopPropagation();
        setSectionSettingsBlock(block);
      }} className="p-1.5 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 text-gray-600" title="Section Settings" data-id="element-644">
            <SettingsIcon className="h-3.5 w-3.5" data-id="element-645" />
          </button>}
        {block.type === 'columns' && <button onClick={e => {
        e.stopPropagation();
        setColumnConfigModal({
          isOpen: true,
          blockId: block.id,
          currentCount: block.columnCount || 2
        });
      }} className="p-1.5 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 text-gray-600" title="Configure Columns" data-id="element-646">
            <SettingsIcon className="h-3.5 w-3.5" data-id="element-647" />
          </button>}
        <button onClick={e => {
        e.stopPropagation();
        duplicateBlock(block.id);
      }} className="p-1.5 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 text-gray-600" title="Duplicate" data-id="element-648">
          <CopyIcon className="h-3.5 w-3.5" data-id="element-649" />
        </button>
        <button onClick={e => {
        e.stopPropagation();
        deleteBlock(block.id);
      }} className="p-1.5 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-red-50 text-gray-600" title="Delete" data-id="element-650">
          <Trash2Icon className="h-3.5 w-3.5 text-red-500" data-id="element-651" />
        </button>
      </div>
      {/* Section drop zone — render children inline with action buttons */}
      {block.type === 'section' ? <div onDragOver={e => {
      e.preventDefault();
      e.stopPropagation();
    }} onDrop={handleSectionDrop(block.id)} data-id="element-652">
          {/* Section header */}
          {(() => {
        const showTitle = block.sectionShowTitle !== false;
        const anim = block.sectionAnimation && block.sectionAnimation !== 'none' ? block.sectionAnimation : null;
        const borderClass = block.sectionBorderStyle === 'dashed' ? 'border-dashed' : block.sectionBorderStyle === 'accent' ? 'border-l-4 border-l-blue-500' : block.sectionBorderStyle === 'none' ? 'border-transparent' : '';
        return <div className={`rounded-lg border border-gray-200 overflow-hidden ${borderClass}`} style={{
          background: block.sectionBackground || undefined
        }} data-id="element-653">
                {showTitle && <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200" data-id="element-654">
                    <LayoutListIcon className="h-4 w-4 text-blue-500 flex-shrink-0" data-id="element-655" />
                    <span className="text-sm font-semibold flex-1" style={{
              color: theme.headingColor
            }} data-id="element-656">
                      {block.sectionTitle || 'Section'}
                    </span>
                    {anim && <span className="text-[9px] px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded-full font-medium" data-id="element-657">
                        {anim}
                      </span>}
                  </div>}
                {!showTitle && <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50/50 border-b border-gray-100" data-id="element-658">
                    <LayoutListIcon className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" data-id="element-659" />
                    <span className="text-[10px] text-gray-400 italic flex-1" data-id="element-660">
                      {block.sectionTitle || 'Section'} (title hidden)
                    </span>
                    {anim && <span className="text-[9px] px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded-full font-medium" data-id="element-661">
                        {anim}
                      </span>}
                  </div>}
                <div className="p-3" data-id="element-662">
                  {block.children && block.children.length > 0 ? <div className="space-y-2" data-id="element-663">
                      {block.children.map(child => <div key={child.id} className={`group/child text-xs text-gray-600 bg-white rounded-md px-3 py-2 border transition-colors cursor-pointer flex items-center gap-2 ${selectedId === child.id ? 'border-blue-400 bg-blue-50/30' : 'border-gray-200 hover:border-gray-300'}`} onClick={e => {
                e.stopPropagation();
                setSelectedId(child.id);
              }} data-id="element-664">
                          <GripVerticalIcon className="h-3 w-3 text-gray-300 flex-shrink-0" data-id="element-665" />
                          <span className="flex-1 truncate" data-id="element-666">
                            {child.label || child.type}
                          </span>
                          <span className="text-[9px] text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded flex-shrink-0" data-id="element-667">
                            {child.type}
                          </span>
                          {/* Inline action buttons — visible on hover */}
                          <div className="flex items-center gap-0.5 opacity-0 group-hover/child:opacity-100 transition-opacity flex-shrink-0" data-id="element-668">
                            <button onClick={e => {
                    e.stopPropagation();
                    setSelectedId(child.id);
                  }} className="p-1 rounded hover:bg-blue-100 transition-colors" title="Edit field properties" data-id="element-669">
                              <PencilIcon className="h-3 w-3 text-blue-500" data-id="element-670" />
                            </button>
                            <button onClick={e => {
                    e.stopPropagation();
                    removeChildFromSection(block.id, child.id);
                  }} className="p-1 rounded hover:bg-red-100 transition-colors" title="Remove from section" data-id="element-671">
                              <XIcon className="h-3 w-3 text-red-400" data-id="element-672" />
                            </button>
                          </div>
                        </div>)}
                    </div> : null}
                  <div className="border-2 border-dashed border-gray-200 rounded-md p-3 text-center mt-2 hover:border-blue-300 hover:bg-blue-50/30 transition-colors" data-id="element-673">
                    <p className="text-[10px] text-gray-400" data-id="element-674">
                      Drop fields here
                    </p>
                  </div>
                </div>
              </div>;
      })()}
        </div> : block.type === 'columns' ? <div data-id="element-675">
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200 rounded-t-lg" data-id="element-676">
            <ColumnsIcon className="h-4 w-4 text-blue-500 flex-shrink-0" data-id="element-677" />
            <span className="text-sm font-semibold flex-1" style={{
          color: theme.headingColor
        }} data-id="element-678">
              {block.columnCount || 2} Columns
            </span>
          </div>
          <div className="grid gap-3 p-3" style={{
        gridTemplateColumns: `repeat(${block.columnCount || 2}, 1fr)`
      }} data-id="element-679">
            {Array.from({
          length: block.columnCount || 2
        }).map((_, colIdx) => {
          const colBlocks = block.columns?.[colIdx] || [];
          const isOver = dragOverCol?.parentId === block.id && dragOverCol?.colIndex === colIdx;
          return <div key={colIdx} onDragOver={handleColumnDragOver(block.id, colIdx)} onDragLeave={() => setDragOverCol(null)} onDrop={handleColumnDrop(block.id, colIdx)} className={`min-h-[60px] border-2 border-dashed rounded-md p-2 transition-colors ${isOver ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`} data-id="element-680">
                  {colBlocks.length > 0 ? <div className="space-y-1.5" data-id="element-681">
                      {colBlocks.map(child => <div key={child.id} className={`group/child text-xs text-gray-600 bg-white rounded-md px-2.5 py-1.5 border transition-colors cursor-pointer flex items-center gap-1.5 ${selectedChild?.childId === child.id ? 'border-blue-400 bg-blue-50/30' : 'border-gray-200 hover:border-gray-300'}`} onClick={e => {
                e.stopPropagation();
                setSelectedChild({
                  parentId: block.id,
                  colIndex: colIdx,
                  childId: child.id
                });
                setSelectedId(null);
              }} data-id="element-682">
                          <span className="flex-1 truncate" data-id="element-683">
                            {child.label || child.type}
                          </span>
                          <span className="text-[9px] text-gray-400 bg-gray-50 px-1 py-0.5 rounded flex-shrink-0" data-id="element-684">
                            {child.type}
                          </span>
                          <button onClick={e => {
                  e.stopPropagation();
                  deleteChildBlock(block.id, colIdx, child.id);
                }} className="p-0.5 rounded hover:bg-red-100 transition-colors opacity-0 group-hover/child:opacity-100 flex-shrink-0" title="Remove" data-id="element-685">
                            <XIcon className="h-3 w-3 text-red-400" data-id="element-686" />
                          </button>
                        </div>)}
                    </div> : <div className="flex items-center justify-center h-full text-[10px] text-gray-400" data-id="element-687">
                      Drop here
                    </div>}
                </div>;
        })}
          </div>
        </div> : <CanvasBlockPreview block={block} theme={theme} data-id="element-688" />}
    </div>;
  return <>
      <div className="flex h-[calc(100vh-220px)] min-h-[500px] border border-gray-200 rounded-lg overflow-hidden bg-white" data-id="element-689">
        {/* Left Panel — Block Palette */}
        <div className="w-60 flex-shrink-0 border-r border-gray-200 bg-gray-50 overflow-y-auto" data-id="element-690">
          <div className="p-3 border-b border-gray-200" data-id="element-691">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-692">
              Blocks
            </h3>
          </div>
          {categories.map(cat => <div key={cat} data-id="element-693">
              <button onClick={() => toggleCat(cat)} className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:bg-gray-100" data-id="element-694">
                {cat}
                <ChevronRightIcon className={`h-3 w-3 transition-transform ${collapsedCats.has(cat) ? '' : 'rotate-90'}`} data-id="element-695" />
              </button>
              {!collapsedCats.has(cat) && <div className="px-2 pb-2 space-y-1" data-id="element-696">
                  {activePalette.filter(p => p.category === cat).map(item => {
              const Icon = ICONS[item.icon] || TypeIcon;
              return <div key={item.type} draggable onDragStart={handlePaletteDragStart(item.type as FormBlockType)} onDragEnd={handleDragEnd} className="flex items-center gap-2 px-2.5 py-2 rounded-md bg-white border border-gray-200 text-sm text-gray-700 cursor-grab hover:border-blue-300 hover:shadow-sm active:cursor-grabbing select-none" data-id="element-697">
                          <Icon className="h-4 w-4 text-gray-400 flex-shrink-0" data-id="element-698" />
                          <span className="truncate" data-id="element-699">{item.label}</span>
                        </div>;
            })}
                </div>}
            </div>)}
        </div>

        {/* Center — Canvas */}
        <div className="flex-1 overflow-y-auto bg-gray-100 p-6" onClick={() => setSelectedId(null)} data-id="element-700">
          <div className="flex items-center justify-between mb-4" data-id="element-701">
            <div className="flex items-center gap-1" data-id="element-702">
              <button onClick={undo} disabled={historyIdx <= 0} className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30" title="Undo (Ctrl+Z)" data-id="element-703">
                <UndoIcon className="h-4 w-4 text-gray-500" data-id="element-704" />
              </button>
              <button onClick={redo} disabled={historyIdx >= history.length - 1} className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30" title="Redo (Ctrl+Shift+Z)" data-id="element-705">
                <RedoIcon className="h-4 w-4 text-gray-500" data-id="element-706" />
              </button>
            </div>

            {/* AI Beautify + Logic Rules */}
            <div className="flex items-center gap-2" data-id="element-707">
              {/* Logic Rules button — hidden for landing pages */}
              {!hideLogicRules && <button onClick={() => setShowLogicEditor(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors" data-id="element-708">
                  <ZapIcon className="h-3.5 w-3.5" data-id="element-709" />
                  Logic Rules
                  {rules.length > 0 && <span className="ml-0.5 text-[10px] font-semibold bg-purple-200 text-purple-800 px-1.5 py-0.5 rounded-full" data-id="element-710">
                      {rules.length}
                    </span>}
                </button>}

              {showBeautifyInput && <div className="flex items-center gap-1.5 bg-white border border-purple-200 rounded-lg px-2 py-1 shadow-sm" data-id="element-711">
                  <input type="text" value={beautifyPrompt} onChange={e => setBeautifyPrompt(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter') handleBeautify(beautifyPrompt);
                if (e.key === 'Escape') {
                  setShowBeautifyInput(false);
                  setBeautifyPrompt('');
                }
              }} placeholder="e.g. dark mode, playful, corporate..." className="text-xs border-none outline-none bg-transparent w-48 placeholder:text-gray-400" autoFocus data-id="element-712" />
                  <button onClick={() => handleBeautify(beautifyPrompt)} disabled={isBeautifying} className="p-1 text-purple-600 hover:text-purple-800 disabled:opacity-50" data-id="element-713">
                    <SendIcon className="h-3.5 w-3.5" data-id="element-714" />
                  </button>
                  <button onClick={() => {
                setShowBeautifyInput(false);
                setBeautifyPrompt('');
              }} className="p-1 text-gray-400 hover:text-gray-600" data-id="element-715">
                    <XIcon className="h-3.5 w-3.5" data-id="element-716" />
                  </button>
                </div>}
              <button onClick={() => {
              if (showBeautifyInput) handleBeautify(beautifyPrompt);else if (blocks.length > 0) setShowBeautifyInput(true);
            }} disabled={isBeautifying || blocks.length === 0} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 disabled:opacity-50 transition-colors" title="Use AI to beautify your form's styling" data-id="element-717">
                {isBeautifying ? <Loader2Icon className="h-3.5 w-3.5 animate-spin" data-id="element-718" /> : <WandSparklesIcon className="h-3.5 w-3.5" data-id="element-719" />}
                {isBeautifying ? 'Beautifying...' : '✨ AI Beautify'}
              </button>
              <span className="text-xs text-gray-400" data-id="element-720">
                {blocks.length} block{blocks.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          <div ref={canvasRef} className="max-w-xl mx-auto rounded-lg min-h-[300px]" style={{
          fontFamily: theme.fontFamily,
          background: theme.backgroundType === 'gradient' ? `linear-gradient(${theme.gradientDirection || 'to bottom right'}, ${theme.gradientFrom || '#667eea'}, ${theme.gradientTo || '#764ba2'})` : theme.backgroundColor || '#ffffff',
          boxShadow: theme.containerShadow === 'lg' ? '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)' : theme.containerShadow === 'md' ? '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)' : theme.containerShadow === 'sm' ? '0 1px 2px 0 rgba(0,0,0,0.05)' : '0 1px 3px 0 rgba(0,0,0,0.1)',
          border: theme.containerBorder ? `1px solid ${theme.inputBorderColor || '#e5e7eb'}` : '1px solid #e5e7eb',
          borderRadius: (theme.borderRadius || 6) + 4
        }} data-id="element-721">
            <div style={{
            padding: theme.formPadding || 32
          }} data-id="element-722">
              {blocks.length === 0 && <div onDragOver={e => {
              e.preventDefault();
              setDragOverIdx(0);
            }} onDragLeave={handleDragLeave} onDrop={handleCanvasDropEnd} className={`border-2 border-dashed rounded-lg py-20 px-8 text-center transition-colors ${dragOverIdx === 0 ? 'border-blue-400 bg-blue-50' : 'border-gray-300'}`} data-id="element-723">
                  <p className="text-sm text-gray-400" data-id="element-724">
                    Drag blocks here to start building your form
                  </p>
                </div>}

              {canvasRows.map((row, rowIdx) => {
              // Get the flat index of the first block in this row for the drop zone above
              const firstFlatIdx = row.blocks[0].flatIndex;
              if (row.type === 'half-row') {
                return <Fragment key={row.blocks.map(b => b.block.id).join('-')} data-id="element-725">
                      {/* Drop zone above the row */}
                      <div onDragOver={handleDragOver(firstFlatIdx)} onDragLeave={handleDragLeave} onDrop={handleDrop(firstFlatIdx)} className="relative transition-all" style={{
                    height: isDragging ? 16 : 6,
                    marginTop: isDragging ? 2 : 0,
                    marginBottom: isDragging ? 2 : 0
                  }} data-id="element-726">
                        {dragOverIdx === firstFlatIdx && <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1 bg-blue-500 rounded-full shadow-sm" data-id="element-727">
                            <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-blue-500 rounded-full" data-id="element-728" />
                            <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-blue-500 rounded-full" data-id="element-729" />
                          </div>}
                      </div>
                      {/* Two-column grid row */}
                      <div className="grid grid-cols-2 gap-3" style={{
                    marginBottom: (theme.fieldSpacing || 20) - 16
                  }} data-id="element-730">
                        {row.blocks.map(item => renderBlockCard(item.block, item.flatIndex))}
                        {/* If only one half-width block, show empty slot */}
                        {row.blocks.length === 1 && <div onDragOver={e => {
                      e.preventDefault();
                      setDragOverIdx(row.blocks[0].flatIndex + 1);
                    }} onDragLeave={handleDragLeave} onDrop={handleDrop(row.blocks[0].flatIndex + 1)} className={`border-2 border-dashed rounded-md p-6 flex items-center justify-center text-xs text-gray-400 transition-colors ${dragOverIdx === row.blocks[0].flatIndex + 1 ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`} data-id="element-731">
                            Drop a half-width field here
                          </div>}
                      </div>
                    </Fragment>;
              }
              // Full-width block
              const item = row.blocks[0];
              return <Fragment key={item.block.id} data-id="element-732">
                    <div onDragOver={handleDragOver(item.flatIndex)} onDragLeave={handleDragLeave} onDrop={handleDrop(item.flatIndex)} className="relative transition-all" style={{
                  height: isDragging ? 16 : 6,
                  marginTop: isDragging ? 2 : 0,
                  marginBottom: isDragging ? 2 : 0
                }} data-id="element-733">
                      {dragOverIdx === item.flatIndex && <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1 bg-blue-500 rounded-full shadow-sm" data-id="element-734">
                          <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-blue-500 rounded-full" data-id="element-735" />
                          <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-blue-500 rounded-full" data-id="element-736" />
                        </div>}
                    </div>
                    <div style={{
                  marginBottom: (theme.fieldSpacing || 20) - 16
                }} data-id="element-737">
                      {renderBlockCard(item.block, item.flatIndex)}
                    </div>
                  </Fragment>;
            })}

              {blocks.length > 0 && <div onDragOver={e => {
              e.preventDefault();
              setDragOverIdx(blocks.length);
            }} onDragLeave={handleDragLeave} onDrop={handleDrop(blocks.length)} className="relative transition-all" style={{
              height: isDragging ? 40 : 16
            }} data-id="element-738">
                  {dragOverIdx === blocks.length && <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1 bg-blue-500 rounded-full shadow-sm" data-id="element-739">
                      <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-blue-500 rounded-full" data-id="element-740" />
                      <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-blue-500 rounded-full" data-id="element-741" />
                    </div>}
                </div>}
            </div>
          </div>
        </div>

        {/* Right Panel — Settings + Properties */}
        <div className="w-72 flex-shrink-0 border-l border-gray-200 bg-white flex flex-col" data-id="element-742">
          {settingsSidebar && <div className="border-b border-gray-200 p-3 flex-shrink-0" data-id="element-743">
              {settingsSidebar}
            </div>}
          <div className="border-b border-gray-200 px-4 py-2" data-id="element-744">
            <span className="text-xs font-medium text-gray-900" data-id="element-745">Design</span>
          </div>
          <div className="flex-1 overflow-y-auto" data-id="element-746">
            {selectedBlock ? <BlockProperties block={selectedBlock} onChange={updates => updateBlock(selectedBlock.id, updates)} onBlur={commitToHistory} onDelete={() => deleteBlock(selectedBlock.id)} data-id="element-747" /> : <ThemeProperties theme={theme} onChange={setTheme} data-id="element-748" />}
          </div>
        </div>
      </div>

      {/* Full-screen Logic Rules Editor overlay */}
      {showLogicEditor && <LogicRulesEditor blocks={blocks} rules={rules} onChange={newRules => {
      setRules(newRules);
      onSchemaChange(blocks, theme, newRules);
    }} onClose={() => setShowLogicEditor(false)} data-id="element-749" />}

      {/* Column Configuration Modal */}
      {columnConfigModal && columnConfigModal.isOpen && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-750">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-751">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setColumnConfigModal(null)} data-id="element-752" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm p-6" data-id="element-753">
              <div className="flex items-center justify-between mb-4" data-id="element-754">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-755">
                  Configure Columns
                </h3>
                <button onClick={() => setColumnConfigModal(null)} className="text-gray-400 hover:text-gray-500" data-id="element-756">
                  <XIcon className="h-5 w-5" data-id="element-757" />
                </button>
              </div>
              <div className="space-y-4" data-id="element-758">
                <div data-id="element-759">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-760">
                    Number of Columns
                  </label>
                  <div className="flex gap-2" data-id="element-761">
                    {[2, 3, 4].map(num => <button key={num} onClick={() => {
                  const block = blocks.find(b => b.id === columnConfigModal.blockId);
                  if (block) {
                    // Ensure we have enough column arrays
                    const currentCols = block.columns || [[], []];
                    const newCols = Array(num).fill(null).map((_, i) => currentCols[i] || []);
                    updateBlock(block.id, {
                      columnCount: num,
                      columns: newCols
                    });
                  }
                  setColumnConfigModal(null);
                }} className={`flex-1 py-2 px-4 border rounded-md text-sm font-medium transition-colors ${columnConfigModal.currentCount === num ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-762">
                        {num}
                      </button>)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* Section Settings Modal */}
      {sectionSettingsBlock && <SectionSettingsModal block={sectionSettingsBlock} onSave={updates => {
      updateBlock(sectionSettingsBlock.id, updates);
      commitToHistory();
      setSectionSettingsBlock(null);
    }} onClose={() => setSectionSettingsBlock(null)} data-id="element-763" />}
    </>;
}
// ── Canvas Block Preview ──
function CanvasBlockPreview({
  block,
  theme
}: {
  block: FormBlock;
  theme: FormTheme;
}) {
  switch (block.type) {
    case 'heading':
      const Tag = `h${block.level || 2}` as keyof JSX.IntrinsicElements;
      return <Tag className="font-semibold" style={{
        fontSize: block.level === 1 ? 24 : block.level === 3 ? 16 : 20,
        color: theme.headingColor
      }} data-id="element-764">
          {block.content || 'Heading'}
        </Tag>;
    case 'paragraph':
      return <p className="text-sm" style={{
        color: theme.paragraphColor
      }} data-id="element-765">
          {block.content || 'Paragraph text'}
        </p>;
    case 'divider':
      return <hr className="border-gray-200" data-id="element-766" />;
    case 'spacer':
      return <div className="flex items-center justify-center text-xs text-gray-400" style={{
        height: block.height || 24
      }} data-id="element-767">
          ↕ {block.height || 24}px
        </div>;
    case 'video_embed':
      {
        if (!block.videoUrl) {
          return <div className="bg-gray-100 border-2 border-dashed border-gray-300 rounded p-6 text-center text-sm text-gray-400" data-id="element-768">
            Video / GIF — set URL in properties
          </div>;
        }
        const isMp4 = block.videoUrl.toLowerCase().endsWith('.mp4');
        const isGif = block.videoUrl.toLowerCase().endsWith('.gif');
        const isYoutube = block.videoUrl.includes('youtube.com') || block.videoUrl.includes('youtu.be');
        const isVimeo = block.videoUrl.includes('vimeo.com');
        if (isGif) {
          return <img src={block.videoUrl} alt="GIF" className="w-full h-auto rounded-md" data-id="element-769" />;
        }
        if (isMp4) {
          return <video src={block.videoUrl} controls autoPlay={block.videoAutoplay} muted={block.videoMuted} loop={block.videoLoop} className="w-full h-auto rounded-md" data-id="element-770" />;
        }
        // YouTube / Vimeo iframe
        let embedUrl = block.videoUrl;
        if (isYoutube) {
          const videoId = block.videoUrl.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?]+)/)?.[1];
          if (videoId) {
            embedUrl = `https://www.youtube.com/embed/${videoId}?rel=0${block.videoAutoplay ? '&autoplay=1' : ''}${block.videoMuted ? '&mute=1' : ''}${block.videoLoop ? `&loop=1&playlist=${videoId}` : ''}`;
          }
        } else if (isVimeo) {
          const videoId = block.videoUrl.match(/vimeo\.com\/(?:video\/)?([0-9]+)/)?.[1];
          if (videoId) {
            embedUrl = `https://player.vimeo.com/video/${videoId}?title=0&byline=0&portrait=0${block.videoAutoplay ? '&autoplay=1' : ''}${block.videoMuted ? '&muted=1' : ''}${block.videoLoop ? '&loop=1' : ''}`;
          }
        }
        const ratioClass = block.videoAspectRatio === '4:3' ? 'aspect-[4/3]' : block.videoAspectRatio === '1:1' ? 'aspect-square' : block.videoAspectRatio === '9:16' ? 'aspect-[9/16]' : 'aspect-video'; // 16:9 default
        return <div className={`w-full relative ${ratioClass} bg-gray-100 rounded-md overflow-hidden`} data-id="element-771">
          <iframe src={embedUrl} className="absolute top-0 left-0 w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-772" />
        </div>;
      }
    case 'recaptcha':
      return <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-md max-w-[300px]" data-id="element-773">
          <div className="flex items-center gap-3" data-id="element-774">
            <div className="w-6 h-6 rounded-sm border-2 border-gray-300 bg-white" data-id="element-775"></div>
            <span className="text-sm font-medium text-gray-700" data-id="element-776">
              I'm not a robot
            </span>
          </div>
          <div className="flex flex-col items-center" data-id="element-777">
            <ShieldCheckIcon className="h-6 w-6 text-blue-500 mb-1" data-id="element-778" />
            <span className="text-[10px] text-gray-500" data-id="element-779">reCAPTCHA</span>
          </div>
        </div>;
    case 'image':
      {
        if (!block.src) {
          return <div className="bg-gray-100 border-2 border-dashed border-gray-300 rounded p-6 text-center text-sm text-gray-400" data-id="element-780">
            Image — set URL in properties
          </div>;
        }
        const sizing = block.imageSizing || 'maxWidth';
        const imgStyle: React.CSSProperties = {
          display: 'block'
        };
        if (sizing === 'natural') {
          // No constraints — natural dimensions
        } else if (sizing === 'maxWidth') {
          imgStyle.maxWidth = `${block.imageMaxWidth || 100}%`;
          imgStyle.height = 'auto';
        } else if (sizing === 'custom') {
          if (block.imageWidth) imgStyle.width = `${block.imageWidth}px`;
          if (block.imageHeight) imgStyle.height = `${block.imageHeight}px`;
          if (block.imageMaxWidth) imgStyle.maxWidth = `${block.imageMaxWidth}%`;
        }
        if (block.imageMaxHeight) imgStyle.maxHeight = `${block.imageMaxHeight}px`;
        if (block.imageObjectFit) imgStyle.objectFit = block.imageObjectFit as any;
        if (block.imageBorderRadius) imgStyle.borderRadius = `${block.imageBorderRadius}px`;
        if (block.imageOpacity != null && block.imageOpacity < 100) imgStyle.opacity = block.imageOpacity / 100;
        if (block.imageShadow === 'sm') imgStyle.boxShadow = '0 1px 2px 0 rgba(0,0,0,0.05)';else if (block.imageShadow === 'md') imgStyle.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.1)';else if (block.imageShadow === 'lg') imgStyle.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.1)';
        // Alignment margin
        const imgAlign = block.imageAlign || 'center';
        if (imgAlign === 'center') imgStyle.margin = '0 auto';else if (imgAlign === 'right') imgStyle.marginLeft = 'auto';
        // Wrapper styles for position
        const wrapStyle: React.CSSProperties = {
          textAlign: imgAlign
        };
        if (block.imagePosition && block.imagePosition !== 'static') {
          wrapStyle.position = block.imagePosition as any;
          if (block.imageTopOffset != null) wrapStyle.top = `${block.imageTopOffset}px`;
          if (block.imageLeftOffset != null) wrapStyle.left = `${block.imageLeftOffset}px`;
          if (block.imagePosition === 'sticky') wrapStyle.zIndex = 10;
          if (block.imagePosition === 'fixed') wrapStyle.zIndex = 1000;
          if (block.imagePosition === 'absolute') wrapStyle.zIndex = 10;
        }
        return <div style={wrapStyle} data-id="element-781">
          <img src={block.src} alt={block.alt || ''} style={imgStyle} data-id="element-782" />
        </div>;
      }
    case 'submit':
      return <div style={{
        textAlign: block.buttonAlign === 'center' ? 'center' : block.buttonAlign === 'right' ? 'right' : 'left'
      }} data-id="element-783">
          <button className={`px-5 py-2.5 text-sm font-semibold rounded-md ${block.buttonAlign === 'full' ? 'w-full' : ''}`} style={{
          backgroundColor: theme.buttonStyle === 'outline' ? 'transparent' : block.buttonColor || theme.primaryColor,
          color: theme.buttonStyle === 'outline' ? block.buttonColor || theme.primaryColor : theme.buttonTextColor,
          border: theme.buttonStyle === 'outline' ? `2px solid ${block.buttonColor || theme.primaryColor}` : 'none',
          borderRadius: theme.borderRadius,
          boxShadow: theme.buttonShadow ? '0 4px 14px 0 rgba(0,0,0,0.15)' : 'none'
        }} data-id="element-784">
            {block.buttonText || 'Submit'}
          </button>
        </div>;
    case 'consent':
      return <div data-id="element-785">
          <label className="flex items-start gap-2 cursor-pointer" data-id="element-786">
            <input type="checkbox" disabled className="mt-0.5" style={{
            accentColor: theme.primaryColor
          }} data-id="element-787" />
            <span className="text-sm" style={{
            color: theme.labelColor,
            lineHeight: 1.4
          }} data-id="element-788">
              {block.consentText || 'I agree to receive updates'}
              {block.required && <span className="text-red-500 ml-0.5" data-id="element-789">*</span>}
            </span>
          </label>
          {block.helpText && <p className="text-xs text-gray-400 mt-1 ml-6" data-id="element-790">{block.helpText}</p>}
        </div>;
    case 'hidden':
      return <div className="text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded font-mono" data-id="element-791">
          hidden: {block.name} = {block.defaultValue || '(empty)'}
        </div>;
    case 'section':
      {
        const showTitle = block.sectionShowTitle !== false;
        const anim = block.sectionAnimation && block.sectionAnimation !== 'none' ? block.sectionAnimation : null;
        const borderClass = block.sectionBorderStyle === 'dashed' ? 'border-dashed' : block.sectionBorderStyle === 'accent' ? 'border-l-4 border-l-blue-500' : block.sectionBorderStyle === 'none' ? 'border-transparent' : '';
        return <div className={`rounded-lg border border-gray-200 overflow-hidden ${borderClass}`} style={{
          background: block.sectionBackground || undefined
        }} data-id="element-792">
          {showTitle && <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200" data-id="element-793">
              <LayoutListIcon className="h-4 w-4 text-blue-500 flex-shrink-0" data-id="element-794" />
              <span className="text-sm font-semibold flex-1" style={{
              color: theme.headingColor
            }} data-id="element-795">
                {block.sectionTitle || 'Section'}
              </span>
              {anim && <span className="text-[9px] px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded-full font-medium" data-id="element-796">
                  {anim}
                </span>}
            </div>}
          {!showTitle && <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50/50 border-b border-gray-100" data-id="element-797">
              <LayoutListIcon className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" data-id="element-798" />
              <span className="text-[10px] text-gray-400 italic flex-1" data-id="element-799">
                {block.sectionTitle || 'Section'} (title hidden)
              </span>
              {anim && <span className="text-[9px] px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded-full font-medium" data-id="element-800">
                  {anim}
                </span>}
            </div>}
          <div className="p-3" data-id="element-801">
            {block.children && block.children.length > 0 ? <div className="space-y-2" data-id="element-802">
                {block.children.map(child => <div key={child.id} className="text-xs text-gray-600 bg-white rounded-md px-3 py-2 border border-gray-200 flex items-center gap-2 group/child" data-id="element-803">
                    <GripVerticalIcon className="h-3 w-3 text-gray-300 flex-shrink-0" data-id="element-804" />
                    <span className="flex-1" data-id="element-805">{child.label || child.type}</span>
                    <span className="text-[9px] text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded" data-id="element-806">
                      {child.type}
                    </span>
                  </div>)}
              </div> : null}
            <div className="border-2 border-dashed border-gray-200 rounded-md p-3 text-center mt-2 hover:border-blue-300 hover:bg-blue-50/30 transition-colors" data-id="element-807">
              <p className="text-[10px] text-gray-400" data-id="element-808">Drop fields here</p>
            </div>
          </div>
        </div>;
      }
    case 'subform':
      {
        const columns = block.subformColumns || [{
          label: 'Column 1',
          type: 'text'
        }, {
          label: 'Column 2',
          type: 'text'
        }];
        const showName = block.subformShowName !== false;
        const minR = block.subformMinRows || 1;
        const maxR = block.subformMaxRows || 10;
        return <div className="rounded-lg border border-gray-200 overflow-hidden bg-white" data-id="element-809">
          {showName ? <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200" data-id="element-810">
              <TableIcon className="h-4 w-4 text-blue-500 flex-shrink-0" data-id="element-811" />
              <span className="text-sm font-semibold flex-1" style={{
              color: theme.headingColor
            }} data-id="element-812">
                {block.subformName || 'Subform'}
              </span>
              <span className="text-[9px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-813">
                {minR}–{maxR} rows
              </span>
            </div> : <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50/50 border-b border-gray-100" data-id="element-814">
              <TableIcon className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" data-id="element-815" />
              <span className="text-[10px] text-gray-400 italic flex-1" data-id="element-816">
                {block.subformName || 'Subform'} (name hidden)
              </span>
              <span className="text-[9px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-817">
                {minR}–{maxR} rows
              </span>
            </div>}
          <div className="overflow-x-auto" data-id="element-818">
            <table className="w-full text-left border-collapse" data-id="element-819">
              <thead data-id="element-820">
                <tr className="border-b border-gray-200 bg-gray-50/50" data-id="element-821">
                  <th className="px-3 py-2 text-xs font-medium text-gray-500 w-8" data-id="element-822">
                    #
                  </th>
                  {columns.map((col, i) => <th key={i} className="px-3 py-2 text-xs font-medium text-gray-500" data-id="element-823">
                      {col.label}
                      {col.required && <span className="text-red-500 ml-0.5" data-id="element-824">*</span>}
                    </th>)}
                  <th className="px-3 py-2 w-8" data-id="element-825"></th>
                </tr>
              </thead>
              <tbody data-id="element-826">
                <tr className="border-b border-gray-100" data-id="element-827">
                  <td className="px-3 py-1.5 text-gray-400" data-id="element-828">1</td>
                  {columns.map((col, i) => <td key={i} className="px-3 py-1.5" data-id="element-829">
                      <div className="w-full px-2 py-1 rounded text-gray-400 text-xs" style={{
                      border: `1px solid ${theme.inputBorderColor}`,
                      background: theme.inputBackground,
                      borderRadius: theme.borderRadius
                    }} data-id="element-830">
                        {col.placeholder || col.type}
                      </div>
                    </td>)}
                  <td className="px-3 py-1.5" data-id="element-831">
                    <Trash2Icon className="h-3 w-3 text-gray-300" data-id="element-832" />
                  </td>
                </tr>
                <tr className="border-b border-gray-100" data-id="element-833">
                  <td className="px-3 py-1.5 text-gray-400" data-id="element-834">2</td>
                  {columns.map((_, i) => <td key={i} className="px-3 py-1.5" data-id="element-835">
                      <div className="w-full px-2 py-1 rounded text-gray-300" style={{
                      border: `1px dashed ${theme.inputBorderColor}`,
                      background: theme.inputBackground,
                      borderRadius: theme.borderRadius
                    }} data-id="element-836">
                        &nbsp;
                      </div>
                    </td>)}
                  <td className="px-3 py-1.5" data-id="element-837">
                    <Trash2Icon className="h-3 w-3 text-gray-300" data-id="element-838" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100" data-id="element-839">
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-400 cursor-default" data-id="element-840">
              <PlusIcon className="h-3 w-3" data-id="element-841" /> Add Row
            </span>
            <span className="text-[9px] text-gray-400" data-id="element-842">
              {columns.length} field{columns.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>;
      }
    case 'phone':
      {
        const defaultCountry = block.phoneDefaultCountry || 'US';
        const country = COUNTRY_CODES.find(c => c.iso === defaultCountry) || COUNTRY_CODES.find(c => c.iso === 'US')!;
        return <div data-id="element-843">
          <div className="flex items-center gap-1 mb-1" data-id="element-844">
            <label className="block" style={{
              fontSize: theme.labelFontSize,
              fontWeight: Number(theme.labelFontWeight),
              color: theme.labelColor
            }} data-id="element-845">
              {block.label}
              {block.required && <span className="text-red-500 ml-0.5" data-id="element-846">*</span>}
            </label>
            {block.width === 'half' && <span className="text-[10px] px-1 py-0.5 bg-blue-50 text-blue-600 rounded font-medium" data-id="element-847">
                ½
              </span>}
          </div>
          <div className="w-full flex items-stretch text-sm text-gray-400 overflow-hidden" style={{
            borderRadius: theme.borderRadius,
            border: `1px solid ${theme.inputBorderColor}`,
            background: theme.inputBackground,
            boxShadow: theme.inputShadow ? '0 1px 3px 0 rgba(0,0,0,0.08)' : 'none'
          }} data-id="element-848">
            <div className="flex items-center gap-1.5 px-3 bg-gray-50/50 border-r" style={{
              borderColor: theme.inputBorderColor
            }} data-id="element-849">
              <span data-id="element-850">{country.flag}</span>
              <span className="text-gray-600 font-medium" data-id="element-851">
                {country.dialCode}
              </span>
              <ChevronDownIcon className="h-3 w-3 text-gray-400 ml-1" data-id="element-852" />
            </div>
            <div className="flex-1 px-3 py-2 flex items-center" data-id="element-853">
              {block.placeholder || '(555) 000-0000'}
            </div>
          </div>
          {block.helpText && <p className="text-xs text-gray-400 mt-1" data-id="element-854">{block.helpText}</p>}
        </div>;
      }
    default:
      return <div data-id="element-855">
          <div className="flex items-center gap-1 mb-1" data-id="element-856">
            <label className="block" style={{
            fontSize: theme.labelFontSize,
            fontWeight: Number(theme.labelFontWeight),
            color: theme.labelColor
          }} data-id="element-857">
              {block.label}
              {block.required && <span className="text-red-500 ml-0.5" data-id="element-858">*</span>}
            </label>
            {block.width === 'half' && <span className="text-[10px] px-1 py-0.5 bg-blue-50 text-blue-600 rounded font-medium" data-id="element-859">
                ½
              </span>}
          </div>
          {block.type === 'textarea' ? <div className="w-full h-20 px-3 py-2 text-sm text-gray-400" style={{
          borderRadius: theme.borderRadius,
          border: `1px solid ${theme.inputBorderColor}`,
          background: theme.inputBackground,
          boxShadow: theme.inputShadow ? '0 1px 3px 0 rgba(0,0,0,0.08)' : 'none'
        }} data-id="element-860">
              {block.placeholder}
            </div> : block.type === 'select' ? <div className="w-full px-3 py-2 text-sm text-gray-400" style={{
          borderRadius: theme.borderRadius,
          border: `1px solid ${theme.inputBorderColor}`,
          background: theme.inputBackground
        }} data-id="element-861">
              {block.placeholder || 'Select...'}
            </div> : block.type === 'radio' || block.type === 'checkbox' ? <div className={`mt-1 ${block.optionsLayout === 'horizontal' ? 'flex flex-wrap gap-x-4 gap-y-1' : block.optionsColumns && block.optionsColumns > 1 ? `grid gap-x-4 gap-y-1` : 'space-y-1'}`} style={block.optionsColumns && block.optionsColumns > 1 && block.optionsLayout !== 'horizontal' ? {
          gridTemplateColumns: `repeat(${block.optionsColumns}, minmax(0, 1fr))`
        } : undefined} data-id="element-862">
              {(block.validation.options || []).map((opt, i) => <label key={i} className="flex items-center gap-2 text-sm text-gray-600" data-id="element-863">
                  <input type={block.type === 'radio' ? 'radio' : 'checkbox'} disabled className="accent-blue-600" style={{
              accentColor: theme.primaryColor
            }} data-id="element-864" />
                  {opt.label}
                </label>)}
            </div> : <div className="w-full px-3 py-2 text-sm text-gray-400" style={{
          borderRadius: theme.borderRadius,
          border: `1px solid ${theme.inputBorderColor}`,
          background: theme.inputBackground,
          boxShadow: theme.inputShadow ? '0 1px 3px 0 rgba(0,0,0,0.08)' : 'none'
        }} data-id="element-865">
              {block.placeholder || block.type}
            </div>}
          {block.helpText && <p className="text-xs text-gray-400 mt-1" data-id="element-866">{block.helpText}</p>}
        </div>;
  }
}
// ── Block Properties Panel ──
function BlockProperties({
  block,
  onChange,
  onBlur,
  onDelete
}: {
  block: FormBlock;
  onChange: (updates: Partial<FormBlock>) => void;
  onBlur: () => void;
  onDelete: () => void;
}) {
  const isInput = !['heading', 'paragraph', 'divider', 'spacer', 'image', 'submit', 'hidden', 'consent', 'section', 'subform'].includes(block.type);
  const hasOptions = ['select', 'radio', 'checkbox'].includes(block.type);
  return <div className="p-4 space-y-4" data-id="element-867">
      <div className="flex items-center justify-between pb-3 border-b border-gray-200" data-id="element-868">
        <h3 className="text-sm font-semibold text-gray-900 capitalize" data-id="element-869">
          {block.type === 'consent' ? 'Consent / Opt-in' : block.type.replace('_', ' ')}{' '}
          Properties
        </h3>
        <button onClick={onDelete} className="p-1 text-red-500 hover:bg-red-50 rounded" title="Delete block" data-id="element-870">
          <Trash2Icon className="h-4 w-4" data-id="element-871" />
        </button>
      </div>

      {(block.type === 'heading' || block.type === 'paragraph') && <>
          <PropField label="Content" data-id="element-872">
            <textarea value={block.content || ''} onChange={e => onChange({
          content: e.target.value
        })} onBlur={onBlur} rows={3} className="prop-input" data-id="element-873" />
          </PropField>
          {block.type === 'heading' && <PropField label="Level" data-id="element-874">
              <select value={block.level || 2} onChange={e => {
          onChange({
            level: Number(e.target.value) as 1 | 2 | 3 | 4
          });
          onBlur();
        }} className="prop-input" data-id="element-875">
                <option value={1} data-id="element-876">H1</option>
                <option value={2} data-id="element-877">H2</option>
                <option value={3} data-id="element-878">H3</option>
                <option value={4} data-id="element-879">H4</option>
              </select>
            </PropField>}
        </>}

      {block.type === 'spacer' && <PropField label="Height (px)" data-id="element-880">
          <input type="number" value={block.height || 24} onChange={e => onChange({
        height: Number(e.target.value)
      })} onBlur={onBlur} min={4} max={200} className="prop-input" data-id="element-881" />
        </PropField>}

      {block.type === 'image' && <ImageBlockProperties block={block} onChange={onChange} onBlur={onBlur} data-id="element-882" />}

      {block.type === 'video_embed' && <>
          <FProp label="Video URL (YouTube/Vimeo)" data-id="element-883">
            <input type="url" value={block.videoUrl || ''} onChange={e => onChange({
          videoUrl: e.target.value
        })} onBlur={onBlur} className="prop-input" placeholder="https://..." data-id="element-884" />
          </FProp>
          <FProp label="Aspect Ratio" data-id="element-885">
            <select value={block.videoAspectRatio || '16:9'} onChange={e => {
          onChange({
            videoAspectRatio: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-886">
              <option value="16:9" data-id="element-887">16:9 Widescreen</option>
              <option value="4:3" data-id="element-888">4:3 Standard</option>
              <option value="1:1" data-id="element-889">1:1 Square</option>
              <option value="9:16" data-id="element-890">9:16 Vertical</option>
            </select>
          </FProp>
          <div className="space-y-2 pt-2" data-id="element-891">
            <label className="flex items-center gap-2 text-sm text-gray-700" data-id="element-892">
              <input type="checkbox" checked={block.videoAutoplay || false} onChange={e => {
            onChange({
              videoAutoplay: e.target.checked
            });
            onBlur();
          }} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-893" />
              Autoplay
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700" data-id="element-894">
              <input type="checkbox" checked={block.videoMuted ?? true} onChange={e => {
            onChange({
              videoMuted: e.target.checked
            });
            onBlur();
          }} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-895" />
              Muted
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700" data-id="element-896">
              <input type="checkbox" checked={block.videoLoop || false} onChange={e => {
            onChange({
              videoLoop: e.target.checked
            });
            onBlur();
          }} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-897" />
              Loop
            </label>
          </div>
        </>}

      {block.type === 'recaptcha' && <>
          <FProp label="Site Key" data-id="element-898">
            <input type="text" value={block.recaptchaSiteKey || ''} onChange={e => onChange({
          recaptchaSiteKey: e.target.value
        })} onBlur={onBlur} className="prop-input" placeholder="Google reCAPTCHA v2 Site Key" data-id="element-899" />
          </FProp>
          <FProp label="Theme" data-id="element-900">
            <select value={block.recaptchaTheme || 'light'} onChange={e => {
          onChange({
            recaptchaTheme: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-901">
              <option value="light" data-id="element-902">Light</option>
              <option value="dark" data-id="element-903">Dark</option>
            </select>
          </FProp>
          <FProp label="Size" data-id="element-904">
            <select value={block.recaptchaSize || 'normal'} onChange={e => {
          onChange({
            recaptchaSize: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-905">
              <option value="normal" data-id="element-906">Normal</option>
              <option value="compact" data-id="element-907">Compact</option>
            </select>
          </FProp>
        </>}

      {block.type === 'submit' && <>
          <PropField label="Button Text" data-id="element-908">
            <input type="text" value={block.buttonText || ''} onChange={e => onChange({
          buttonText: e.target.value
        })} onBlur={onBlur} className="prop-input" data-id="element-909" />
          </PropField>
          <PropField label="Button Color" data-id="element-910">
            <ColorInput value={block.buttonColor || ''} onChange={v => onChange({
          buttonColor: v
        })} data-id="element-911" />
            <p className="text-[10px] text-gray-400 mt-1" data-id="element-912">
              Leave empty to use theme primary color
            </p>
          </PropField>
          <PropField label="Alignment" data-id="element-913">
            <select value={block.buttonAlign || 'full'} onChange={e => {
          onChange({
            buttonAlign: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-914">
              <option value="left" data-id="element-915">Left</option>
              <option value="center" data-id="element-916">Center</option>
              <option value="right" data-id="element-917">Right</option>
              <option value="full" data-id="element-918">Full Width</option>
            </select>
          </PropField>
          <PropField label="Position Behavior" data-id="element-919">
            <select value={block.buttonPosition || 'static'} onChange={e => {
          onChange({
            buttonPosition: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-920">
              <option value="static" data-id="element-921">Default (flows with form)</option>
              <option value="sticky" data-id="element-922">Sticky (stays visible on scroll)</option>
              <option value="fixed" data-id="element-923">Fixed (always at bottom of screen)</option>
            </select>
          </PropField>
          {(block.buttonPosition === 'sticky' || block.buttonPosition === 'fixed') && <PropField label="Bottom Offset (px)" data-id="element-924">
              <div className="flex items-center gap-2" data-id="element-925">
                <input type="range" min={0} max={60} value={block.buttonStickyOffset || 0} onChange={e => onChange({
            buttonStickyOffset: Number(e.target.value)
          })} onMouseUp={onBlur} className="flex-1" data-id="element-926" />
                <span className="text-xs text-gray-500 w-8 text-right" data-id="element-927">
                  {block.buttonStickyOffset || 0}px
                </span>
              </div>
            </PropField>}
          {block.buttonPosition && block.buttonPosition !== 'static' && <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5" data-id="element-928">
              <p className="text-[10px] text-amber-700 leading-relaxed" data-id="element-929">
                <strong data-id="element-930">
                  {block.buttonPosition === 'sticky' ? 'Sticky' : 'Fixed'}:
                </strong>{' '}
                {block.buttonPosition === 'sticky' ? 'Button stays visible at the bottom while scrolling through the form.' : 'Button is pinned to the bottom of the viewport at all times.'}
              </p>
            </div>}
        </>}

      {block.type === 'consent' && <>
          <PropField label="Consent Text" data-id="element-931">
            <textarea value={block.consentText || ''} onChange={e => onChange({
          consentText: e.target.value
        })} onBlur={onBlur} rows={3} className="prop-input" placeholder="I agree to receive updates and communications" data-id="element-932" />
          </PropField>
          <PropField label="Field Name" data-id="element-933">
            <input type="text" value={block.name} onChange={e => onChange({
          name: e.target.value
        })} onBlur={onBlur} className="prop-input font-mono text-xs" data-id="element-934" />
          </PropField>
          <div className="flex items-center justify-between" data-id="element-935">
            <span className="text-xs font-medium text-gray-700" data-id="element-936">Required</span>
            <button onClick={() => {
          onChange({
            required: !block.required
          });
          onBlur();
        }} className={`relative w-9 h-5 rounded-full transition-colors ${block.required ? 'bg-blue-600' : 'bg-gray-300'}`} data-id="element-937">
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${block.required ? 'translate-x-4' : ''}`} data-id="element-938" />
            </button>
          </div>
          <PropField label="Help Text" data-id="element-939">
            <input type="text" value={block.helpText} onChange={e => onChange({
          helpText: e.target.value
        })} onBlur={onBlur} className="prop-input" data-id="element-940" />
          </PropField>
        </>}

      {block.type === 'section' && <>
          <PropField label="Section Title" data-id="element-941">
            <input type="text" value={block.sectionTitle || ''} onChange={e => onChange({
          sectionTitle: e.target.value
        })} onBlur={onBlur} className="prop-input" placeholder="Section Title" data-id="element-942" />
          </PropField>
          <PropField label="Field Name" data-id="element-943">
            <input type="text" value={block.name} onChange={e => onChange({
          name: e.target.value
        })} onBlur={onBlur} className="prop-input font-mono text-xs" data-id="element-944" />
          </PropField>
          <div className="flex items-center justify-between" data-id="element-945">
            <span className="text-xs font-medium text-gray-700" data-id="element-946">
              Show Title
            </span>
            <button onClick={() => {
          onChange({
            sectionShowTitle: !(block.sectionShowTitle !== false)
          });
          onBlur();
        }} className={`relative w-9 h-5 rounded-full transition-colors ${block.sectionShowTitle !== false ? 'bg-blue-600' : 'bg-gray-300'}`} data-id="element-947">
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${block.sectionShowTitle !== false ? 'translate-x-4' : ''}`} data-id="element-948" />
            </button>
          </div>
          <PropField label="Animation" data-id="element-949">
            <select value={block.sectionAnimation || 'none'} onChange={e => {
          onChange({
            sectionAnimation: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-950">
              <option value="none" data-id="element-951">None</option>
              <option value="fadeIn" data-id="element-952">Fade In</option>
              <option value="slideLeft" data-id="element-953">Slide from Left</option>
              <option value="slideRight" data-id="element-954">Slide from Right</option>
              <option value="slideUp" data-id="element-955">Slide from Bottom</option>
              <option value="slideDown" data-id="element-956">Slide from Top</option>
              <option value="zoomIn" data-id="element-957">Zoom In</option>
            </select>
          </PropField>
          {block.children && block.children.length > 0 && <div className="pt-2 border-t border-gray-200" data-id="element-958">
              <p className="text-[10px] text-gray-500 font-medium mb-1" data-id="element-959">
                {block.children.length} field
                {block.children.length !== 1 ? 's' : ''} in this section
              </p>
            </div>}
          <div className="pt-2 border-t border-gray-200" data-id="element-960">
            <p className="text-[10px] text-gray-400" data-id="element-961">
              Drag fields onto this section to group them. Click the ⚙️ gear
              icon for more settings. Use logic rules to show/hide entire
              sections.
            </p>
          </div>
        </>}

      {block.type === 'subform' && <>
          <PropField label="Subform Name" data-id="element-962">
            <input type="text" value={block.subformName || ''} onChange={e => onChange({
          subformName: e.target.value
        })} onBlur={onBlur} className="prop-input" data-id="element-963" />
          </PropField>
          <PropField label="Field Name" data-id="element-964">
            <input type="text" value={block.name} onChange={e => onChange({
          name: e.target.value
        })} onBlur={onBlur} className="prop-input font-mono text-xs" data-id="element-965" />
          </PropField>
          <div className="flex items-center justify-between py-1" data-id="element-966">
            <span className="text-xs font-medium text-gray-700" data-id="element-967">Show Name</span>
            <button onClick={() => {
          onChange({
            subformShowName: !(block.subformShowName !== false)
          });
          onBlur();
        }} className={`relative w-9 h-5 rounded-full transition-colors ${block.subformShowName !== false ? 'bg-blue-600' : 'bg-gray-300'}`} data-id="element-968">
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${block.subformShowName !== false ? 'translate-x-4' : ''}`} data-id="element-969" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3 pt-1" data-id="element-970">
            <PropField label="Min Rows" data-id="element-971">
              <input type="number" value={block.subformMinRows || 1} onChange={e => {
            const val = Math.max(0, Math.min(50, Number(e.target.value)));
            onChange({
              subformMinRows: val
            });
          }} onBlur={onBlur} min={0} max={50} className="prop-input" data-id="element-972" />
            </PropField>
            <PropField label="Max Rows" data-id="element-973">
              <input type="number" value={block.subformMaxRows || 10} onChange={e => {
            const val = Math.max(1, Math.min(100, Number(e.target.value)));
            onChange({
              subformMaxRows: val
            });
          }} onBlur={onBlur} min={1} max={100} className="prop-input" data-id="element-974" />
            </PropField>
          </div>

          {/* Subform Fields/Columns */}
          <div className="space-y-4 pt-3 border-t border-gray-200" data-id="element-975">
            <div className="flex items-center justify-between" data-id="element-976">
              <span className="text-xs font-semibold text-gray-700" data-id="element-977">
                Fields
              </span>
              <button onClick={() => {
            const cols = [...(block.subformColumns || [])];
            cols.push({
              label: `Field ${cols.length + 1}`,
              type: 'text',
              required: false,
              placeholder: ''
            });
            onChange({
              subformColumns: cols
            });
            onBlur();
          }} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5" data-id="element-978">
                <PlusIcon className="h-3 w-3" data-id="element-979" /> Add Field
              </button>
            </div>
            {(block.subformColumns || []).map((col, i) => <div key={i} className="bg-gray-50 rounded-lg p-3 space-y-3 border border-gray-200" data-id="element-980">
                {/* Row 1: Field number header + delete */}
                <div className="flex items-center justify-between" data-id="element-981">
                  <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-982">
                    Field {i + 1}
                  </span>
                  <button onClick={() => {
              const cols = (block.subformColumns || []).filter((_, j) => j !== i);
              onChange({
                subformColumns: cols
              });
              onBlur();
            }} disabled={(block.subformColumns || []).length <= 1} className="p-1 text-gray-400 hover:text-red-500 disabled:opacity-30 rounded hover:bg-red-50 transition-colors" title="Remove field" data-id="element-983">
                    <XIcon className="h-3.5 w-3.5" data-id="element-984" />
                  </button>
                </div>
                {/* Row 2: Label — full width */}
                <div data-id="element-985">
                  <label className="block text-[10px] font-medium text-gray-500 mb-1" data-id="element-986">
                    Label
                  </label>
                  <input type="text" value={col.label} onChange={e => {
              const cols = [...(block.subformColumns || [])];
              cols[i] = {
                ...cols[i],
                label: e.target.value
              };
              onChange({
                subformColumns: cols
              });
            }} onBlur={onBlur} className="prop-input w-full" placeholder="Field label" data-id="element-987" />
                </div>
                {/* Row 3: Type + Required */}
                <div className="flex items-end gap-3" data-id="element-988">
                  <div className="flex-1" data-id="element-989">
                    <label className="block text-[10px] font-medium text-gray-500 mb-1" data-id="element-990">
                      Type
                    </label>
                    <select value={col.type} onChange={e => {
                const cols = [...(block.subformColumns || [])];
                cols[i] = {
                  ...cols[i],
                  type: e.target.value
                };
                onChange({
                  subformColumns: cols
                });
                onBlur();
              }} className="prop-input w-full" data-id="element-991">
                      <option value="text" data-id="element-992">Text</option>
                      <option value="number" data-id="element-993">Number</option>
                      <option value="email" data-id="element-994">Email</option>
                      <option value="date" data-id="element-995">Date</option>
                      <option value="url" data-id="element-996">URL</option>
                      <option value="textarea" data-id="element-997">Textarea</option>
                      <option value="select" data-id="element-998">Select</option>
                    </select>
                  </div>
                  <label className="flex items-center gap-1.5 text-xs text-gray-600 whitespace-nowrap cursor-pointer pb-1.5" data-id="element-999">
                    <input type="checkbox" checked={col.required || false} onChange={e => {
                const cols = [...(block.subformColumns || [])];
                cols[i] = {
                  ...cols[i],
                  required: e.target.checked
                };
                onChange({
                  subformColumns: cols
                });
                onBlur();
              }} className="rounded border-gray-300 text-blue-600 h-3.5 w-3.5" data-id="element-1000" />
                    Required
                  </label>
                </div>
                {/* Row 4: Placeholder — full width */}
                <div data-id="element-1001">
                  <label className="block text-[10px] font-medium text-gray-500 mb-1" data-id="element-1002">
                    Placeholder
                  </label>
                  <input type="text" value={col.placeholder || ''} onChange={e => {
              const cols = [...(block.subformColumns || [])];
              cols[i] = {
                ...cols[i],
                placeholder: e.target.value
              };
              onChange({
                subformColumns: cols
              });
            }} onBlur={onBlur} className="prop-input w-full" placeholder="Placeholder text" data-id="element-1003" />
                </div>
                {col.type === 'select' && <div className="space-y-1.5 pt-1 border-t border-gray-200" data-id="element-1004">
                    <span className="text-xs text-gray-500 font-medium" data-id="element-1005">
                      Options
                    </span>
                    {(col.options || []).map((opt, oi) => <div key={oi} className="flex items-center gap-1.5" data-id="element-1006">
                        <input type="text" value={opt.label} onChange={e => {
                const cols = [...(block.subformColumns || [])];
                const opts = [...(cols[i].options || [])];
                opts[oi] = {
                  label: e.target.value,
                  value: e.target.value
                };
                cols[i] = {
                  ...cols[i],
                  options: opts
                };
                onChange({
                  subformColumns: cols
                });
              }} onBlur={onBlur} className="prop-input flex-1 text-xs" placeholder="Option label" data-id="element-1007" />
                        <button onClick={() => {
                const cols = [...(block.subformColumns || [])];
                const opts = (cols[i].options || []).filter((_, j) => j !== oi);
                cols[i] = {
                  ...cols[i],
                  options: opts
                };
                onChange({
                  subformColumns: cols
                });
                onBlur();
              }} className="p-1 text-gray-400 hover:text-red-500" data-id="element-1008">
                          <XIcon className="h-3 w-3" data-id="element-1009" />
                        </button>
                      </div>)}
                    <button onClick={() => {
              const cols = [...(block.subformColumns || [])];
              const opts = [...(cols[i].options || [])];
              opts.push({
                label: `Option ${opts.length + 1}`,
                value: `option_${opts.length + 1}`
              });
              cols[i] = {
                ...cols[i],
                options: opts
              };
              onChange({
                subformColumns: cols
              });
              onBlur();
            }} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1" data-id="element-1010">
                      <PlusIcon className="h-3 w-3" data-id="element-1011" /> Add Option
                    </button>
                  </div>}
              </div>)}
          </div>

          <div className="pt-3 border-t border-gray-200" data-id="element-1012">
            <p className="text-[10px] text-gray-400 leading-relaxed" data-id="element-1013">
              Subforms allow users to add multiple rows of structured data. Each
              field becomes a column in the table. Subforms cannot be used as
              logic rule triggers but can be shown/hidden by logic rules.
            </p>
          </div>
        </>}

      {block.type === 'hidden' && <>
          <PropField label="Field Name" data-id="element-1014">
            <input type="text" value={block.name} onChange={e => onChange({
          name: e.target.value
        })} onBlur={onBlur} className="prop-input font-mono" data-id="element-1015" />
          </PropField>
          <PropField label="Value" data-id="element-1016">
            <input type="text" value={block.defaultValue} onChange={e => onChange({
          defaultValue: e.target.value
        })} onBlur={onBlur} className="prop-input" data-id="element-1017" />
          </PropField>
        </>}

      {isInput && <>
          <PropField label="Label" data-id="element-1018">
            <input type="text" value={block.label} onChange={e => onChange({
          label: e.target.value,
          name: labelToName(e.target.value) + '_' + block.id.slice(0, 4)
        })} onBlur={onBlur} className="prop-input" data-id="element-1019" />
          </PropField>
          <PropField label="Field Name" data-id="element-1020">
            <input type="text" value={block.name} onChange={e => onChange({
          name: e.target.value
        })} onBlur={onBlur} className="prop-input font-mono text-xs" data-id="element-1021" />
          </PropField>
          {block.type !== 'radio' && block.type !== 'checkbox' && <PropField label="Placeholder" data-id="element-1022">
              <input type="text" value={block.placeholder} onChange={e => onChange({
          placeholder: e.target.value
        })} onBlur={onBlur} className="prop-input" data-id="element-1023" />
            </PropField>}
          <PropField label="Help Text" data-id="element-1024">
            <input type="text" value={block.helpText} onChange={e => onChange({
          helpText: e.target.value
        })} onBlur={onBlur} className="prop-input" data-id="element-1025" />
          </PropField>
          <div className="flex items-center justify-between" data-id="element-1026">
            <span className="text-xs font-medium text-gray-700" data-id="element-1027">Required</span>
            <button onClick={() => {
          onChange({
            required: !block.required
          });
          onBlur();
        }} className={`relative w-9 h-5 rounded-full transition-colors ${block.required ? 'bg-blue-600' : 'bg-gray-300'}`} data-id="element-1028">
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${block.required ? 'translate-x-4' : ''}`} data-id="element-1029" />
            </button>
          </div>
          <PropField label="Width" data-id="element-1030">
            <select value={block.width} onChange={e => {
          onChange({
            width: e.target.value as 'full' | 'half'
          });
          onBlur();
        }} className="prop-input" data-id="element-1031">
              <option value="full" data-id="element-1032">Full Width</option>
              <option value="half" data-id="element-1033">Half Width (side by side)</option>
            </select>
          </PropField>
        </>}

      {(block.type === 'radio' || block.type === 'checkbox') && <div className="space-y-3 pb-3 border-b border-gray-200" data-id="element-1034">
          <PropField label="Options Layout" data-id="element-1035">
            <select value={block.optionsLayout || 'vertical'} onChange={e => {
          onChange({
            optionsLayout: e.target.value as 'vertical' | 'horizontal'
          });
          onBlur();
        }} className="prop-input" data-id="element-1036">
              <option value="vertical" data-id="element-1037">Vertical (stacked)</option>
              <option value="horizontal" data-id="element-1038">Horizontal (inline)</option>
            </select>
          </PropField>
          {block.optionsLayout !== 'horizontal' && <PropField label="Columns" data-id="element-1039">
              <select value={block.optionsColumns || 1} onChange={e => {
          onChange({
            optionsColumns: Number(e.target.value) as 1 | 2 | 3 | 4
          });
          onBlur();
        }} className="prop-input" data-id="element-1040">
                <option value={1} data-id="element-1041">1 column</option>
                <option value={2} data-id="element-1042">2 columns</option>
                <option value={3} data-id="element-1043">3 columns</option>
                <option value={4} data-id="element-1044">4 columns</option>
              </select>
            </PropField>}
        </div>}

      {hasOptions && <div className="space-y-2" data-id="element-1045">
          <div className="flex items-center justify-between" data-id="element-1046">
            <span className="text-xs font-medium text-gray-700" data-id="element-1047">Options</span>
            <button onClick={() => {
          const opts = [...(block.validation.options || [])];
          opts.push({
            label: `Option ${opts.length + 1}`,
            value: `option_${opts.length + 1}`
          });
          onChange({
            validation: {
              ...block.validation,
              options: opts
            }
          });
          onBlur();
        }} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5" data-id="element-1048">
              <PlusIcon className="h-3 w-3" data-id="element-1049" /> Add
            </button>
          </div>
          {(block.validation.options || []).map((opt, i) => <div key={i} className="flex items-center gap-1" data-id="element-1050">
              <input type="text" value={opt.label} onChange={e => {
          const opts = [...(block.validation.options || [])];
          opts[i] = {
            ...opts[i],
            label: e.target.value,
            value: labelToName(e.target.value)
          };
          onChange({
            validation: {
              ...block.validation,
              options: opts
            }
          });
        }} onBlur={onBlur} className="prop-input flex-1 text-xs" placeholder="Label" data-id="element-1051" />
              <button onClick={() => {
          const opts = (block.validation.options || []).filter((_, j) => j !== i);
          onChange({
            validation: {
              ...block.validation,
              options: opts
            }
          });
          onBlur();
        }} className="p-1 text-gray-400 hover:text-red-500" data-id="element-1052">
                <XIcon className="h-3 w-3" data-id="element-1053" />
              </button>
            </div>)}
        </div>}
    </div>;
}
// ── Image Block Properties ──
function VideoEmbedProperties({
  block,
  onChange,
  onBlur
}: {
  block: FormBlock;
  onChange: (updates: Partial<FormBlock>) => void;
  onBlur: () => void;
}) {
  return <div className="space-y-4 p-4" data-id="element-1054">
      <PropField label="Video or GIF URL" data-id="element-1055">
        <input type="url" value={block.videoUrl || ''} onChange={e => onChange({
        videoUrl: e.target.value
      })} onBlur={onBlur} placeholder="https://youtube.com/... or .mp4/.gif" className="prop-input" data-id="element-1056" />
        <p className="text-[10px] text-gray-500 mt-1" data-id="element-1057">
          Supports YouTube, Vimeo, MP4, and GIF URLs.
        </p>
      </PropField>

      <PropField label="Aspect Ratio" data-id="element-1058">
        <select value={block.videoAspectRatio || '16:9'} onChange={e => onChange({
        videoAspectRatio: e.target.value as any
      })} onBlur={onBlur} className="prop-input" data-id="element-1059">
          <option value="16:9" data-id="element-1060">16:9 (Widescreen)</option>
          <option value="4:3" data-id="element-1061">4:3 (Standard)</option>
          <option value="1:1" data-id="element-1062">1:1 (Square)</option>
          <option value="9:16" data-id="element-1063">9:16 (Vertical)</option>
        </select>
      </PropField>

      <div className="space-y-2 pt-2 border-t border-gray-100" data-id="element-1064">
        <label className="flex items-center gap-2 px-1 py-1 text-xs text-gray-700 cursor-pointer" data-id="element-1065">
          <input type="checkbox" checked={block.videoAutoplay || false} onChange={e => onChange({
          videoAutoplay: e.target.checked
        })} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-1066" />
          Autoplay
        </label>
        <label className="flex items-center gap-2 px-1 py-1 text-xs text-gray-700 cursor-pointer" data-id="element-1067">
          <input type="checkbox" checked={block.videoMuted ?? true} onChange={e => onChange({
          videoMuted: e.target.checked
        })} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-1068" />
          Muted
        </label>
        <label className="flex items-center gap-2 px-1 py-1 text-xs text-gray-700 cursor-pointer" data-id="element-1069">
          <input type="checkbox" checked={block.videoLoop || false} onChange={e => onChange({
          videoLoop: e.target.checked
        })} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-1070" />
          Loop
        </label>
        <p className="text-[10px] text-gray-500 leading-tight" data-id="element-1071">
          Note: Most browsers require videos to be muted to autoplay.
        </p>
      </div>
    </div>;
}
function ImageBlockProperties({
  block,
  onChange,
  onBlur
}: {
  block: FormBlock;
  onChange: (updates: Partial<FormBlock>) => void;
  onBlur: () => void;
}) {
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const team = currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaImages, setMediaImages] = useState<MediaLibraryRecord[]>([]);
  const [isLoadingMedia, setIsLoadingMedia] = useState(false);
  const [mediaSearch, setMediaSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'source' | 'layout' | 'effects'>('source');
  const loadMedia = async () => {
    if (mediaImages.length > 0) return;
    setIsLoadingMedia(true);
    try {
      const all = await mediaLibraryApi.list();
      // Filter by team — same logic as MediaLibrary page
      const teamFiltered = all.filter(img => img.Team === team || img.Team === currentWorkspace.name);
      setMediaImages(teamFiltered);
    } catch (err) {
      console.warn('Failed to load media library:', err);
    } finally {
      setIsLoadingMedia(false);
    }
  };
  const filteredMedia = mediaImages.filter(img => {
    if (!mediaSearch) return true;
    const q = mediaSearch.toLowerCase();
    return (img.Image_Name || '').toLowerCase().includes(q) || (img.Category || '').toLowerCase().includes(q);
  });
  const tabs = [{
    id: 'source' as const,
    label: 'Source',
    icon: ImageIcon
  }, {
    id: 'layout' as const,
    label: 'Layout',
    icon: SlidersHorizontalIcon
  }, {
    id: 'effects' as const,
    label: 'Effects',
    icon: SparklesIcon
  }];
  return <>
      {/* Tab bar */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-lg" data-id="element-1072">
        {tabs.map(t => {
        const Icon = t.icon;
        return <button key={t.id} onClick={() => setActiveTab(t.id)} className={`flex-1 flex items-center justify-center gap-1 py-1.5 text-[10px] font-medium rounded-md transition-colors ${activeTab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-1073">
              <Icon className="h-3 w-3" data-id="element-1074" />
              {t.label}
            </button>;
      })}
      </div>

      {activeTab === 'source' && <>
          <PropField label="Image URL" data-id="element-1075">
            <input type="url" value={block.src || ''} onChange={e => onChange({
          src: e.target.value
        })} onBlur={onBlur} placeholder="https://..." className="prop-input" data-id="element-1076" />
          </PropField>

          {/* Media Library Picker */}
          <div data-id="element-1077">
            <button onClick={() => {
          setShowMediaPicker(!showMediaPicker);
          if (!showMediaPicker) loadMedia();
        }} className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors" data-id="element-1078">
              <ImagePlusIcon className="h-3.5 w-3.5" data-id="element-1079" />
              Choose from Media Library
            </button>

            {showMediaPicker && <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden bg-white" data-id="element-1080">
                <div className="p-2 border-b border-gray-100" data-id="element-1081">
                  <div className="relative" data-id="element-1082">
                    <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" data-id="element-1083" />
                    <input type="text" value={mediaSearch} onChange={e => setMediaSearch(e.target.value)} placeholder="Search images..." className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500" data-id="element-1084" />
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto p-2" data-id="element-1085">
                  {isLoadingMedia ? <div className="flex items-center justify-center py-6" data-id="element-1086">
                      <Loader2Icon className="h-5 w-5 text-gray-400 animate-spin" data-id="element-1087" />
                    </div> : filteredMedia.length === 0 ? <div className="text-center py-6" data-id="element-1088">
                      <ImageIcon className="h-6 w-6 text-gray-300 mx-auto mb-1" data-id="element-1089" />
                      <p className="text-[10px] text-gray-400" data-id="element-1090">
                        {mediaSearch ? 'No images match your search' : 'No images in library'}
                      </p>
                    </div> : <div className="grid grid-cols-3 gap-1.5" data-id="element-1091">
                      {filteredMedia.map(img => <button key={img.ID} onClick={() => {
                onChange({
                  src: img.Image_URL,
                  alt: img.Image_Name || img.Description || ''
                });
                onBlur();
                setShowMediaPicker(false);
                setMediaSearch('');
              }} className={`relative group rounded-md overflow-hidden border-2 transition-all aspect-square ${block.src === img.Image_URL ? 'border-blue-500 ring-1 ring-blue-500' : 'border-transparent hover:border-blue-300'}`} data-id="element-1092">
                          <img src={img.Image_URL} alt={img.Image_Name} className="w-full h-full object-cover" onError={e => {
                  ;
                  (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40"><rect fill="%23f3f4f6" width="40" height="40"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%239ca3af" font-size="10">?</text></svg>';
                }} data-id="element-1093" />
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1094">
                            <p className="text-[9px] text-white truncate font-medium" data-id="element-1095">
                              {img.Image_Name}
                            </p>
                          </div>
                        </button>)}
                    </div>}
                </div>
              </div>}
          </div>

          <PropField label="Alt Text" data-id="element-1096">
            <input type="text" value={block.alt || ''} onChange={e => onChange({
          alt: e.target.value
        })} onBlur={onBlur} className="prop-input" placeholder="Describe the image..." data-id="element-1097" />
          </PropField>

          {/* Preview */}
          {block.src && <div className="rounded-lg border border-gray-200 overflow-hidden" data-id="element-1098">
              <img src={block.src} alt={block.alt || ''} className="w-full h-24 object-contain bg-gray-50" onError={e => {
          ;
          (e.target as HTMLImageElement).style.display = 'none';
        }} data-id="element-1099" />
            </div>}
        </>}

      {activeTab === 'layout' && <>
          <PropField label="Alignment" data-id="element-1100">
            <div className="grid grid-cols-3 gap-1" data-id="element-1101">
              {(['left', 'center', 'right'] as const).map(align => <button key={align} onClick={() => {
            onChange({
              imageAlign: align
            });
            onBlur();
          }} className={`py-1.5 text-[10px] font-medium rounded-md border transition-colors capitalize ${(block.imageAlign || 'center') === align ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`} data-id="element-1102">
                  {align}
                </button>)}
            </div>
          </PropField>

          <PropField label="Sizing Mode" data-id="element-1103">
            <select value={block.imageSizing || 'maxWidth'} onChange={e => {
          onChange({
            imageSizing: e.target.value as 'natural' | 'maxWidth' | 'custom'
          });
          onBlur();
        }} className="prop-input" data-id="element-1104">
              <option value="natural" data-id="element-1105">Natural (no constraints)</option>
              <option value="maxWidth" data-id="element-1106">Max Width % (responsive)</option>
              <option value="custom" data-id="element-1107">Custom dimensions</option>
            </select>
          </PropField>

          {(block.imageSizing || 'maxWidth') === 'maxWidth' && <PropField label="Max Width" data-id="element-1108">
              <div className="flex items-center gap-2" data-id="element-1109">
                <input type="range" min={10} max={100} value={block.imageMaxWidth || 100} onChange={e => onChange({
            imageMaxWidth: Number(e.target.value)
          })} onMouseUp={onBlur} className="flex-1" data-id="element-1110" />
                <span className="text-xs text-gray-500 w-10 text-right" data-id="element-1111">
                  {block.imageMaxWidth || 100}%
                </span>
              </div>
            </PropField>}

          {block.imageSizing === 'custom' && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-1112">
                <PropField label="Width (px)" data-id="element-1113">
                  <input type="number" value={block.imageWidth || ''} onChange={e => onChange({
              imageWidth: e.target.value ? Number(e.target.value) : 0
            })} onBlur={onBlur} min={0} placeholder="auto" className="prop-input" data-id="element-1114" />
                </PropField>
                <PropField label="Height (px)" data-id="element-1115">
                  <input type="number" value={block.imageHeight || ''} onChange={e => onChange({
              imageHeight: e.target.value ? Number(e.target.value) : 0
            })} onBlur={onBlur} min={0} placeholder="auto" className="prop-input" data-id="element-1116" />
                </PropField>
              </div>
              <PropField label="Max Width (%)" data-id="element-1117">
                <div className="flex items-center gap-2" data-id="element-1118">
                  <input type="range" min={0} max={100} value={block.imageMaxWidth || 0} onChange={e => onChange({
              imageMaxWidth: Number(e.target.value)
            })} onMouseUp={onBlur} className="flex-1" data-id="element-1119" />
                  <span className="text-xs text-gray-500 w-10 text-right" data-id="element-1120">
                    {block.imageMaxWidth || 0 ? `${block.imageMaxWidth}%` : 'none'}
                  </span>
                </div>
              </PropField>
            </>}

          {(block.imageSizing === 'custom' || block.imageSizing === 'maxWidth') && <PropField label="Max Height (px)" data-id="element-1121">
              <input type="number" value={block.imageMaxHeight || ''} onChange={e => onChange({
          imageMaxHeight: e.target.value ? Number(e.target.value) : 0
        })} onBlur={onBlur} min={0} placeholder="none" className="prop-input" data-id="element-1122" />
            </PropField>}

          {block.imageSizing === 'natural' && <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5" data-id="element-1123">
              <p className="text-[10px] text-amber-700 leading-relaxed" data-id="element-1124">
                <strong data-id="element-1125">Natural:</strong> The image will render at its original
                dimensions with no constraints. It may overflow the form
                container if it's larger.
              </p>
            </div>}

          <PropField label="Object Fit" data-id="element-1126">
            <select value={block.imageObjectFit || 'contain'} onChange={e => {
          onChange({
            imageObjectFit: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-1127">
              <option value="contain" data-id="element-1128">Contain (fit inside)</option>
              <option value="cover" data-id="element-1129">Cover (fill & crop)</option>
              <option value="fill" data-id="element-1130">Fill (stretch)</option>
              <option value="none" data-id="element-1131">None (natural size)</option>
              <option value="scale-down" data-id="element-1132">
                Scale Down (smaller of contain/none)
              </option>
            </select>
            {(block.imageObjectFit === 'cover' || block.imageObjectFit === 'contain') && !block.imageHeight && !block.imageMaxHeight && (block.imageSizing || 'maxWidth') !== 'natural' && <p className="text-[10px] text-gray-400 mt-1" data-id="element-1133">
                  Set a height or max-height to see {block.imageObjectFit} take
                  effect.
                </p>}
          </PropField>

          <PropField label="Position" data-id="element-1134">
            <select value={block.imagePosition || 'static'} onChange={e => {
          onChange({
            imagePosition: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-1135">
              <option value="static" data-id="element-1136">Static (normal flow)</option>
              <option value="relative" data-id="element-1137">Relative (offset from normal)</option>
              <option value="absolute" data-id="element-1138">Absolute (relative to form)</option>
              <option value="fixed" data-id="element-1139">Fixed (relative to viewport)</option>
              <option value="sticky" data-id="element-1140">Sticky (sticks on scroll)</option>
            </select>
          </PropField>

          {(block.imagePosition === 'absolute' || block.imagePosition === 'fixed' || block.imagePosition === 'relative') && <>
              <PropField label="Top Offset (px)" data-id="element-1141">
                <div className="flex items-center gap-2" data-id="element-1142">
                  <input type="number" value={block.imageTopOffset || 0} onChange={e => onChange({
              imageTopOffset: Number(e.target.value)
            })} onBlur={onBlur} className="prop-input" data-id="element-1143" />
                </div>
              </PropField>
              <PropField label="Left Offset (px)" data-id="element-1144">
                <div className="flex items-center gap-2" data-id="element-1145">
                  <input type="number" value={block.imageLeftOffset || 0} onChange={e => onChange({
              imageLeftOffset: Number(e.target.value)
            })} onBlur={onBlur} className="prop-input" data-id="element-1146" />
                </div>
              </PropField>
            </>}

          {block.imagePosition && block.imagePosition !== 'static' && <div className="rounded-lg bg-blue-50 border border-blue-200 p-2.5" data-id="element-1147">
              <p className="text-[10px] text-blue-700 leading-relaxed" data-id="element-1148">
                <strong className="capitalize" data-id="element-1149">{block.imagePosition}:</strong>{' '}
                {block.imagePosition === 'absolute' && 'Positioned relative to the form container. Use offsets to place it.'}
                {block.imagePosition === 'fixed' && 'Stays fixed on the viewport. Great for watermarks or background logos.'}
                {block.imagePosition === 'sticky' && 'Sticks to the top when scrolling past it.'}
                {block.imagePosition === 'relative' && 'Offset from its normal position. Other elements are not affected.'}
              </p>
            </div>}

          <PropField label="Border Radius" data-id="element-1150">
            <div className="flex items-center gap-2" data-id="element-1151">
              <input type="range" min={0} max={50} value={block.imageBorderRadius || 0} onChange={e => onChange({
            imageBorderRadius: Number(e.target.value)
          })} onMouseUp={onBlur} className="flex-1" data-id="element-1152" />
              <span className="text-xs text-gray-500 w-10 text-right" data-id="element-1153">
                {block.imageBorderRadius || 0}px
              </span>
            </div>
          </PropField>
        </>}

      {activeTab === 'effects' && <>
          <PropField label="Opacity" data-id="element-1154">
            <div className="flex items-center gap-2" data-id="element-1155">
              <input type="range" min={10} max={100} value={block.imageOpacity ?? 100} onChange={e => onChange({
            imageOpacity: Number(e.target.value)
          })} onMouseUp={onBlur} className="flex-1" data-id="element-1156" />
              <span className="text-xs text-gray-500 w-10 text-right" data-id="element-1157">
                {block.imageOpacity ?? 100}%
              </span>
            </div>
          </PropField>

          <PropField label="Shadow" data-id="element-1158">
            <div className="grid grid-cols-4 gap-1" data-id="element-1159">
              {(['none', 'sm', 'md', 'lg'] as const).map(s => <button key={s} onClick={() => {
            onChange({
              imageShadow: s
            });
            onBlur();
          }} className={`py-1.5 text-[10px] font-medium rounded-md border transition-colors capitalize ${(block.imageShadow || 'none') === s ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`} data-id="element-1160">
                  {s === 'none' ? 'Off' : s.toUpperCase()}
                </button>)}
            </div>
          </PropField>

          <PropField label="Animation" data-id="element-1161">
            <select value={block.imageAnimation || 'none'} onChange={e => {
          onChange({
            imageAnimation: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-1162">
              <option value="none" data-id="element-1163">None</option>
              <option value="fadeIn" data-id="element-1164">Fade In</option>
              <option value="slideUp" data-id="element-1165">Slide Up</option>
              <option value="slideDown" data-id="element-1166">Slide Down</option>
              <option value="zoomIn" data-id="element-1167">Zoom In</option>
              <option value="blurIn" data-id="element-1168">Blur In (focus reveal)</option>
            </select>
          </PropField>

          {block.imageAnimation && block.imageAnimation !== 'none' && <div className="rounded-lg bg-purple-50 border border-purple-200 p-2.5" data-id="element-1169">
              <p className="text-[10px] text-purple-700 leading-relaxed" data-id="element-1170">
                <strong className="capitalize" data-id="element-1171">{block.imageAnimation}:</strong>{' '}
                {block.imageAnimation === 'fadeIn' && 'Image fades in smoothly when the form loads.'}
                {block.imageAnimation === 'slideUp' && 'Image slides up into view from below.'}
                {block.imageAnimation === 'slideDown' && 'Image slides down into view from above.'}
                {block.imageAnimation === 'zoomIn' && 'Image starts small and zooms to full size.'}
                {block.imageAnimation === 'blurIn' && 'Image starts blurred and gradually comes into focus.'}
              </p>
            </div>}
        </>}
    </>;
}
// ── Section Settings Modal ──
function SectionSettingsModal({
  block,
  onSave,
  onClose
}: {
  block: FormBlock;
  onSave: (updates: Partial<FormBlock>) => void;
  onClose: () => void;
}) {
  const [showTitle, setShowTitle] = useState(block.sectionShowTitle !== false);
  const [animation, setAnimation] = useState(block.sectionAnimation || 'none');
  const [collapsible, setCollapsible] = useState(block.sectionCollapsible || false);
  const [borderStyle, setBorderStyle] = useState(block.sectionBorderStyle || 'solid');
  const [bgColor, setBgColor] = useState(block.sectionBackground || '');
  const [title, setTitle] = useState(block.sectionTitle || '');
  const handleSave = () => {
    onSave({
      sectionTitle: title,
      sectionShowTitle: showTitle,
      sectionAnimation: animation as any,
      sectionCollapsible: collapsible,
      sectionBorderStyle: borderStyle as any,
      sectionBackground: bgColor || undefined
    });
  };
  const animations = [{
    value: 'none',
    label: 'None',
    desc: 'No animation'
  }, {
    value: 'fadeIn',
    label: 'Fade In',
    desc: 'Smoothly fades into view'
  }, {
    value: 'slideLeft',
    label: 'Slide from Left',
    desc: 'Slides in from the left side'
  }, {
    value: 'slideRight',
    label: 'Slide from Right',
    desc: 'Slides in from the right side'
  }, {
    value: 'slideUp',
    label: 'Slide from Bottom',
    desc: 'Slides up from below'
  }, {
    value: 'slideDown',
    label: 'Slide from Top',
    desc: 'Slides down from above'
  }, {
    value: 'zoomIn',
    label: 'Zoom In',
    desc: 'Scales up from small to full size'
  }];
  return <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose} data-id="element-1172">
      <div className="bg-white rounded-xl shadow-2xl w-[480px] max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-1173">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200" data-id="element-1174">
          <div className="flex items-center gap-2.5" data-id="element-1175">
            <div className="p-2 bg-blue-100 rounded-lg" data-id="element-1176">
              <SettingsIcon className="h-4 w-4 text-blue-600" data-id="element-1177" />
            </div>
            <div data-id="element-1178">
              <h3 className="text-sm font-semibold text-gray-900" data-id="element-1179">
                Section Settings
              </h3>
              <p className="text-[11px] text-gray-500" data-id="element-1180">
                Configure how this section appears and behaves
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1181">
            <XIcon className="h-4 w-4" data-id="element-1182" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5" data-id="element-1183">
          {/* Title */}
          <div data-id="element-1184">
            <label className="block text-xs font-semibold text-gray-700 mb-1.5" data-id="element-1185">
              Section Title
            </label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="e.g. Personal Information" data-id="element-1186" />
          </div>

          {/* Show/Hide Title */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-id="element-1187">
            <div className="flex items-center gap-2.5" data-id="element-1188">
              {showTitle ? <EyeIcon className="h-4 w-4 text-gray-500" data-id="element-1189" /> : <EyeOffIcon className="h-4 w-4 text-gray-400" data-id="element-1190" />}
              <div data-id="element-1191">
                <p className="text-xs font-medium text-gray-700" data-id="element-1192">
                  Show Section Title
                </p>
                <p className="text-[10px] text-gray-500" data-id="element-1193">
                  Display the title above the section fields
                </p>
              </div>
            </div>
            <button onClick={() => setShowTitle(!showTitle)} className={`relative w-10 h-5.5 rounded-full transition-colors ${showTitle ? 'bg-blue-600' : 'bg-gray-300'}`} data-id="element-1194">
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${showTitle ? 'translate-x-5' : ''}`} data-id="element-1195" />
            </button>
          </div>

          {/* Collapsible */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-id="element-1196">
            <div className="flex items-center gap-2.5" data-id="element-1197">
              <ChevronDownIcon className="h-4 w-4 text-gray-500" data-id="element-1198" />
              <div data-id="element-1199">
                <p className="text-xs font-medium text-gray-700" data-id="element-1200">Collapsible</p>
                <p className="text-[10px] text-gray-500" data-id="element-1201">
                  Allow users to expand/collapse this section
                </p>
              </div>
            </div>
            <button onClick={() => setCollapsible(!collapsible)} className={`relative w-10 h-5.5 rounded-full transition-colors ${collapsible ? 'bg-blue-600' : 'bg-gray-300'}`} data-id="element-1202">
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${collapsible ? 'translate-x-5' : ''}`} data-id="element-1203" />
            </button>
          </div>

          {/* Border Style */}
          <div data-id="element-1204">
            <label className="block text-xs font-semibold text-gray-700 mb-2" data-id="element-1205">
              Border Style
            </label>
            <div className="grid grid-cols-4 gap-2" data-id="element-1206">
              {[{
              value: 'none',
              label: 'None',
              preview: 'border-transparent'
            }, {
              value: 'solid',
              label: 'Solid',
              preview: 'border-gray-300'
            }, {
              value: 'dashed',
              label: 'Dashed',
              preview: 'border-gray-300 border-dashed'
            }, {
              value: 'accent',
              label: 'Accent',
              preview: 'border-l-4 border-l-blue-500 border-gray-200'
            }].map(opt => <button key={opt.value} onClick={() => setBorderStyle(opt.value)} className={`flex flex-col items-center gap-1.5 p-2.5 rounded-lg border-2 transition-all ${borderStyle === opt.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1207">
                  <div className={`w-full h-6 rounded border ${opt.preview}`} data-id="element-1208" />
                  <span className={`text-[10px] font-medium ${borderStyle === opt.value ? 'text-blue-700' : 'text-gray-600'}`} data-id="element-1209">
                    {opt.label}
                  </span>
                </button>)}
            </div>
          </div>

          {/* Background Color */}
          <div data-id="element-1210">
            <label className="block text-xs font-semibold text-gray-700 mb-1.5" data-id="element-1211">
              Background Color
            </label>
            <div className="flex items-center gap-2" data-id="element-1212">
              <input type="color" value={bgColor || '#ffffff'} onChange={e => setBgColor(e.target.value)} className="h-8 w-8 rounded border border-gray-300 cursor-pointer flex-shrink-0" data-id="element-1213" />
              <input type="text" value={bgColor} onChange={e => setBgColor(e.target.value)} placeholder="Leave empty for transparent" className="flex-1 px-3 py-1.5 text-xs font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1214" />
              {bgColor && <button onClick={() => setBgColor('')} className="text-[10px] text-gray-400 hover:text-red-500 px-1" data-id="element-1215">
                  clear
                </button>}
            </div>
          </div>

          {/* Scroll Animation */}
          <div data-id="element-1216">
            <label className="block text-xs font-semibold text-gray-700 mb-2" data-id="element-1217">
              Scroll Animation
            </label>
            <p className="text-[10px] text-gray-500 mb-3" data-id="element-1218">
              How this section appears when scrolling into view
            </p>
            <div className="space-y-1.5" data-id="element-1219">
              {animations.map(anim => <button key={anim.value} onClick={() => setAnimation(anim.value)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all text-left ${animation === anim.value ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-200' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-1220">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${animation === anim.value ? 'bg-blue-500' : 'bg-gray-200'}`} data-id="element-1221">
                    {animation === anim.value && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-1222">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-1223" />
                      </svg>}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1224">
                    <p className={`text-xs font-medium ${animation === anim.value ? 'text-blue-700' : 'text-gray-700'}`} data-id="element-1225">
                      {anim.label}
                    </p>
                    <p className="text-[10px] text-gray-500" data-id="element-1226">{anim.desc}</p>
                  </div>
                </button>)}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50" data-id="element-1227">
          <button onClick={onClose} className="px-4 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1228">
            Cancel
          </button>
          <button onClick={handleSave} className="px-4 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors" data-id="element-1229">
            Save Settings
          </button>
        </div>
      </div>
    </div>;
}
// ── Theme Properties Panel ──
function ThemeProperties({
  theme,
  onChange
}: {
  theme: FormTheme;
  onChange: (t: FormTheme) => void;
}) {
  const [activeSection, setActiveSection] = useState<string>('presets');
  const applyPreset = (preset: StylePreset) => {
    onChange({
      ...theme,
      ...preset.theme
    });
  };
  const sections = [{
    id: 'presets',
    label: 'Style Presets',
    icon: SwatchBookIcon
  }, {
    id: 'colors',
    label: 'Colors & Background',
    icon: PaletteIcon
  }, {
    id: 'typography',
    label: 'Typography',
    icon: TypeIcon
  }, {
    id: 'inputs',
    label: 'Input Fields',
    icon: AlignLeftIcon
  }, {
    id: 'button',
    label: 'Button',
    icon: SendIcon
  }, {
    id: 'layout',
    label: 'Layout & Spacing',
    icon: MoveVerticalIcon
  }];
  return <div className="divide-y divide-gray-100" data-id="element-1230">
      <div className="p-4 pb-3" data-id="element-1231">
        <div className="flex items-center gap-2" data-id="element-1232">
          <PaletteIcon className="h-4 w-4 text-gray-400" data-id="element-1233" />
          <h3 className="text-sm font-semibold text-gray-900" data-id="element-1234">Form Theme</h3>
        </div>
        <p className="text-xs text-gray-500 mt-1" data-id="element-1235">
          Click a block to edit its properties
        </p>
      </div>

      <div className="px-2 py-2 flex flex-wrap gap-1" data-id="element-1236">
        {sections.map(s => {
        const Icon = s.icon;
        return <button key={s.id} onClick={() => setActiveSection(activeSection === s.id ? '' : s.id)} className={`flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md transition-colors ${activeSection === s.id ? 'bg-gray-900 text-white' : 'text-gray-600 bg-gray-50 hover:bg-gray-100'}`} data-id="element-1237">
              <Icon className="h-3 w-3" data-id="element-1238" />
              {s.label}
            </button>;
      })}
      </div>

      {activeSection === 'presets' && <div className="p-4 space-y-3" data-id="element-1239">
          <p className="text-xs text-gray-500" data-id="element-1240">
            Click a preset to instantly apply a complete style.
          </p>
          <div className="grid grid-cols-2 gap-2" data-id="element-1241">
            {FORM_STYLE_PRESETS.map(preset => <button key={preset.name} onClick={() => applyPreset(preset)} className="flex flex-col items-center gap-1.5 p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all text-center group" data-id="element-1242">
                <span className="text-lg" data-id="element-1243">{preset.emoji}</span>
                <span className="text-[11px] font-medium text-gray-700 group-hover:text-blue-700" data-id="element-1244">
                  {preset.name}
                </span>
                <div className="flex gap-0.5" data-id="element-1245">
                  <div className="w-3 h-3 rounded-full border border-gray-200" style={{
              background: preset.theme.primaryColor || theme.primaryColor
            }} data-id="element-1246" />
                  <div className="w-3 h-3 rounded-full border border-gray-200" style={{
              background: preset.theme.backgroundColor || theme.backgroundColor
            }} data-id="element-1247" />
                  <div className="w-3 h-3 rounded-full border border-gray-200" style={{
              background: preset.theme.labelColor || theme.labelColor
            }} data-id="element-1248" />
                </div>
              </button>)}
          </div>
        </div>}

      {activeSection === 'colors' && <div className="p-4 space-y-4" data-id="element-1249">
          <PropField label="Primary Color" data-id="element-1250">
            <ColorInput value={theme.primaryColor} onChange={v => onChange({
          ...theme,
          primaryColor: v
        })} data-id="element-1251" />
          </PropField>
          <PropField label="Background Type" data-id="element-1252">
            <select value={theme.backgroundType} onChange={e => onChange({
          ...theme,
          backgroundType: e.target.value as 'solid' | 'gradient'
        })} className="prop-input" data-id="element-1253">
              <option value="solid" data-id="element-1254">Solid Color</option>
              <option value="gradient" data-id="element-1255">Gradient</option>
            </select>
          </PropField>
          {theme.backgroundType === 'solid' ? <PropField label="Background Color" data-id="element-1256">
              <ColorInput value={theme.backgroundColor} onChange={v => onChange({
          ...theme,
          backgroundColor: v
        })} data-id="element-1257" />
            </PropField> : <>
              <PropField label="Gradient From" data-id="element-1258">
                <ColorInput value={theme.gradientFrom} onChange={v => onChange({
            ...theme,
            gradientFrom: v
          })} data-id="element-1259" />
              </PropField>
              <PropField label="Gradient To" data-id="element-1260">
                <ColorInput value={theme.gradientTo} onChange={v => onChange({
            ...theme,
            gradientTo: v
          })} data-id="element-1261" />
              </PropField>
              <PropField label="Direction" data-id="element-1262">
                <select value={theme.gradientDirection} onChange={e => onChange({
            ...theme,
            gradientDirection: e.target.value as any
          })} className="prop-input" data-id="element-1263">
                  <option value="to bottom" data-id="element-1264">Top → Bottom</option>
                  <option value="to right" data-id="element-1265">Left → Right</option>
                  <option value="to bottom right" data-id="element-1266">Diagonal ↘</option>
                  <option value="to top right" data-id="element-1267">Diagonal ↗</option>
                </select>
              </PropField>
            </>}
          <PropField label="Heading Color" data-id="element-1268">
            <ColorInput value={theme.headingColor} onChange={v => onChange({
          ...theme,
          headingColor: v
        })} data-id="element-1269" />
          </PropField>
          <PropField label="Paragraph Color" data-id="element-1270">
            <ColorInput value={theme.paragraphColor} onChange={v => onChange({
          ...theme,
          paragraphColor: v
        })} data-id="element-1271" />
          </PropField>
        </div>}

      {activeSection === 'typography' && <div className="p-4 space-y-4" data-id="element-1272">
          <PropField label="Font Family" data-id="element-1273">
            <select value={theme.fontFamily} onChange={e => onChange({
          ...theme,
          fontFamily: e.target.value
        })} className="prop-input" data-id="element-1274">
              <option value="Inter, system-ui, sans-serif" data-id="element-1275">Inter</option>
              <option value="Arial, Helvetica, sans-serif" data-id="element-1276">Arial</option>
              <option value="Georgia, serif" data-id="element-1277">Georgia</option>
              <option value="'Courier New', monospace" data-id="element-1278">Courier New</option>
              <option value="'Segoe UI', system-ui, sans-serif" data-id="element-1279">
                Segoe UI
              </option>
              <option value="'Trebuchet MS', sans-serif" data-id="element-1280">Trebuchet MS</option>
              <option value="Verdana, sans-serif" data-id="element-1281">Verdana</option>
              <option value="'Times New Roman', serif" data-id="element-1282">Times New Roman</option>
              <option value="Tahoma, sans-serif" data-id="element-1283">Tahoma</option>
              <option value="'Palatino Linotype', serif" data-id="element-1284">Palatino</option>
            </select>
          </PropField>
          <PropField label="Label Color" data-id="element-1285">
            <ColorInput value={theme.labelColor} onChange={v => onChange({
          ...theme,
          labelColor: v
        })} data-id="element-1286" />
          </PropField>
          <PropField label="Label Font Size" data-id="element-1287">
            <div className="flex items-center gap-2" data-id="element-1288">
              <input type="range" min={11} max={18} value={theme.labelFontSize} onChange={e => onChange({
            ...theme,
            labelFontSize: Number(e.target.value)
          })} className="flex-1" data-id="element-1289" />
              <span className="text-xs text-gray-500 w-8 text-right" data-id="element-1290">
                {theme.labelFontSize}px
              </span>
            </div>
          </PropField>
          <PropField label="Label Weight" data-id="element-1291">
            <select value={theme.labelFontWeight} onChange={e => onChange({
          ...theme,
          labelFontWeight: e.target.value as any
        })} className="prop-input" data-id="element-1292">
              <option value="400" data-id="element-1293">Normal (400)</option>
              <option value="500" data-id="element-1294">Medium (500)</option>
              <option value="600" data-id="element-1295">Semibold (600)</option>
              <option value="700" data-id="element-1296">Bold (700)</option>
            </select>
          </PropField>
        </div>}

      {activeSection === 'inputs' && <div className="p-4 space-y-4" data-id="element-1297">
          <PropField label="Border Color" data-id="element-1298">
            <ColorInput value={theme.inputBorderColor} onChange={v => onChange({
          ...theme,
          inputBorderColor: v
        })} data-id="element-1299" />
          </PropField>
          <PropField label="Input Background" data-id="element-1300">
            <ColorInput value={theme.inputBackground} onChange={v => onChange({
          ...theme,
          inputBackground: v
        })} data-id="element-1301" />
          </PropField>
          <PropField label="Focus Ring Color" data-id="element-1302">
            <ColorInput value={theme.inputFocusColor} onChange={v => onChange({
          ...theme,
          inputFocusColor: v
        })} data-id="element-1303" />
          </PropField>
          <PropField label="Border Radius" data-id="element-1304">
            <div className="flex items-center gap-2" data-id="element-1305">
              <input type="range" min={0} max={20} value={theme.borderRadius} onChange={e => onChange({
            ...theme,
            borderRadius: Number(e.target.value)
          })} className="flex-1" data-id="element-1306" />
              <span className="text-xs text-gray-500 w-8 text-right" data-id="element-1307">
                {theme.borderRadius}px
              </span>
            </div>
          </PropField>
          <ToggleField label="Input Shadow" value={theme.inputShadow} onChange={v => onChange({
        ...theme,
        inputShadow: v
      })} data-id="element-1308" />
        </div>}

      {activeSection === 'button' && <div className="p-4 space-y-4" data-id="element-1309">
          <PropField label="Button Style" data-id="element-1310">
            <select value={theme.buttonStyle} onChange={e => onChange({
          ...theme,
          buttonStyle: e.target.value as 'filled' | 'outline'
        })} className="prop-input" data-id="element-1311">
              <option value="filled" data-id="element-1312">Filled</option>
              <option value="outline" data-id="element-1313">Outline</option>
            </select>
          </PropField>
          <PropField label="Button Text Color" data-id="element-1314">
            <ColorInput value={theme.buttonTextColor} onChange={v => onChange({
          ...theme,
          buttonTextColor: v
        })} data-id="element-1315" />
          </PropField>
          <ToggleField label="Button Shadow" value={theme.buttonShadow} onChange={v => onChange({
        ...theme,
        buttonShadow: v
      })} data-id="element-1316" />
        </div>}

      {activeSection === 'layout' && <div className="p-4 space-y-4" data-id="element-1317">
          <PropField label="Layout Mode" data-id="element-1318">
            <div className="grid grid-cols-2 gap-2" data-id="element-1319">
              <button onClick={() => onChange({
            ...theme,
            layoutMode: 'standard'
          })} className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all text-center ${theme.layoutMode !== 'card' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1320">
                <svg width="28" height="20" viewBox="0 0 28 20" fill="none" className="text-gray-500" data-id="element-1321">
                  <rect x="1" y="1" width="26" height="4" rx="1" fill="currentColor" opacity="0.3" data-id="element-1322" />
                  <rect x="1" y="7" width="26" height="4" rx="1" fill="currentColor" opacity="0.3" data-id="element-1323" />
                  <rect x="1" y="13" width="26" height="4" rx="1" fill="currentColor" opacity="0.3" data-id="element-1324" />
                </svg>
                <span className={`text-[10px] font-semibold ${theme.layoutMode !== 'card' ? 'text-blue-700' : 'text-gray-600'}`} data-id="element-1325">
                  Standard
                </span>
              </button>
              <button onClick={() => onChange({
            ...theme,
            layoutMode: 'card'
          })} className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all text-center ${theme.layoutMode === 'card' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1326">
                <svg width="28" height="20" viewBox="0 0 28 20" fill="none" className="text-gray-500" data-id="element-1327">
                  <rect x="3" y="2" width="22" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" data-id="element-1328" />
                  <rect x="7" y="6" width="14" height="3" rx="1" fill="currentColor" opacity="0.3" data-id="element-1329" />
                  <circle cx="10" cy="14" r="1.5" fill="currentColor" opacity="0.4" data-id="element-1330" />
                  <circle cx="14" cy="14" r="1.5" fill="currentColor" opacity="0.2" data-id="element-1331" />
                  <circle cx="18" cy="14" r="1.5" fill="currentColor" opacity="0.2" data-id="element-1332" />
                </svg>
                <span className={`text-[10px] font-semibold ${theme.layoutMode === 'card' ? 'text-blue-700' : 'text-gray-600'}`} data-id="element-1333">
                  Card
                </span>
              </button>
            </div>
          </PropField>
          {theme.layoutMode === 'card' && <>
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 mb-1" data-id="element-1334">
                <p className="text-[11px] text-blue-700 leading-relaxed" data-id="element-1335">
                  <strong data-id="element-1336">Card mode</strong> shows one question at a time with
                  navigation and a progress bar. Customize below.
                </p>
              </div>

              <PropField label={`Card Width: ${theme.cardWidth || 720}px`} data-id="element-1337">
                <input type="range" min={400} max={1200} step={20} value={theme.cardWidth || 720} onChange={e => onChange({
            ...theme,
            cardWidth: parseInt(e.target.value, 10)
          })} className="w-full accent-blue-600" data-id="element-1338" />
                <div className="flex justify-between text-[10px] text-gray-400 mt-0.5" data-id="element-1339">
                  <span data-id="element-1340">400px</span>
                  <span data-id="element-1341">1200px</span>
                </div>
              </PropField>

              <PropField label="Navigation Style" data-id="element-1342">
                <div className="grid grid-cols-3 gap-1.5" data-id="element-1343">
                  {(['text', 'icons', 'pills'] as const).map(style => <button key={style} onClick={() => onChange({
              ...theme,
              cardNavStyle: style
            })} className={`py-1.5 px-2 text-[10px] font-semibold rounded-md border-2 transition-all capitalize ${theme.cardNavStyle === style ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`} data-id="element-1344">
                      {style === 'text' ? '← Text →' : style === 'icons' ? '‹ Icons ›' : '⬤ Pills'}
                    </button>)}
                </div>
              </PropField>

              {theme.cardNavStyle !== 'icons' && <div className="grid grid-cols-2 gap-3" data-id="element-1345">
                  <PropField label="Previous Label" data-id="element-1346">
                    <input type="text" value={theme.cardPrevLabel ?? '← Back'} onChange={e => onChange({
              ...theme,
              cardPrevLabel: e.target.value
            })} className="prop-input" placeholder="← Back" data-id="element-1347" />
                  </PropField>
                  <PropField label="Next Label" data-id="element-1348">
                    <input type="text" value={theme.cardNextLabel ?? 'Next →'} onChange={e => onChange({
              ...theme,
              cardNextLabel: e.target.value
            })} className="prop-input" placeholder="Next →" data-id="element-1349" />
                  </PropField>
                </div>}

              <PropField label="Transition" data-id="element-1350">
                <select value={theme.cardTransition ?? 'fade'} onChange={e => onChange({
            ...theme,
            cardTransition: e.target.value as 'fade' | 'slide' | 'none'
          })} className="prop-input" data-id="element-1351">
                  <option value="fade" data-id="element-1352">Fade Up</option>
                  <option value="slide" data-id="element-1353">Slide Horizontal</option>
                  <option value="none" data-id="element-1354">Instant (no animation)</option>
                </select>
              </PropField>

              <div className="space-y-2" data-id="element-1355">
                <div className="flex items-center justify-between" data-id="element-1356">
                  <span className="text-xs font-medium text-gray-700" data-id="element-1357">
                    Show Progress Bar
                  </span>
                  <button onClick={() => onChange({
              ...theme,
              cardShowProgress: !(theme.cardShowProgress !== false)
            })} className={`relative w-9 h-5 rounded-full transition-colors ${theme.cardShowProgress !== false ? 'bg-blue-600' : 'bg-gray-300'}`} data-id="element-1358">
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${theme.cardShowProgress !== false ? 'translate-x-4' : ''}`} data-id="element-1359" />
                  </button>
                </div>
                <div className="flex items-center justify-between" data-id="element-1360">
                  <span className="text-xs font-medium text-gray-700" data-id="element-1361">
                    Show Step Counter
                  </span>
                  <button onClick={() => onChange({
              ...theme,
              cardShowCounter: !(theme.cardShowCounter !== false)
            })} className={`relative w-9 h-5 rounded-full transition-colors ${theme.cardShowCounter !== false ? 'bg-blue-600' : 'bg-gray-300'}`} data-id="element-1362">
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${theme.cardShowCounter !== false ? 'translate-x-4' : ''}`} data-id="element-1363" />
                  </button>
                </div>
              </div>

              {theme.cardNavStyle === 'icons' && <div className="rounded-lg bg-gray-50 border border-gray-200 p-2.5" data-id="element-1364">
                  <p className="text-[10px] text-gray-500 leading-relaxed" data-id="element-1365">
                    <strong data-id="element-1366">Icon mode</strong> uses ‹ › arrow icons instead of
                    text labels. Clean and minimal.
                  </p>
                </div>}
              {theme.cardNavStyle === 'pills' && <div className="rounded-lg bg-gray-50 border border-gray-200 p-2.5" data-id="element-1367">
                  <p className="text-[10px] text-gray-500 leading-relaxed" data-id="element-1368">
                    <strong data-id="element-1369">Pill mode</strong> uses rounded pill-shaped buttons
                    with your primary color for the Next button.
                  </p>
                </div>}
            </>}
          <PropField label="Form Padding" data-id="element-1370">
            <div className="flex items-center gap-2" data-id="element-1371">
              <input type="range" min={16} max={64} value={theme.formPadding} onChange={e => onChange({
            ...theme,
            formPadding: Number(e.target.value)
          })} className="flex-1" data-id="element-1372" />
              <span className="text-xs text-gray-500 w-8 text-right" data-id="element-1373">
                {theme.formPadding}px
              </span>
            </div>
          </PropField>
          <PropField label="Field Spacing" data-id="element-1374">
            <div className="flex items-center gap-2" data-id="element-1375">
              <input type="range" min={8} max={40} value={theme.fieldSpacing} onChange={e => onChange({
            ...theme,
            fieldSpacing: Number(e.target.value)
          })} className="flex-1" data-id="element-1376" />
              <span className="text-xs text-gray-500 w-8 text-right" data-id="element-1377">
                {theme.fieldSpacing}px
              </span>
            </div>
          </PropField>
          <PropField label="Container Shadow" data-id="element-1378">
            <select value={theme.containerShadow} onChange={e => onChange({
          ...theme,
          containerShadow: e.target.value as any
        })} className="prop-input" data-id="element-1379">
              <option value="none" data-id="element-1380">None</option>
              <option value="sm" data-id="element-1381">Small</option>
              <option value="md" data-id="element-1382">Medium</option>
              <option value="lg" data-id="element-1383">Large</option>
            </select>
          </PropField>
          <ToggleField label="Container Border" value={theme.containerBorder} onChange={v => onChange({
        ...theme,
        containerBorder: v
      })} data-id="element-1384" />
        </div>}
    </div>;
}
// ── Shared UI Components ──
function PropField({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-1385">
      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-1386">
        {label}
      </label>
      {children}
    </div>;
}
function ColorInput({
  value,
  onChange
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const hexValue = value && value.startsWith('#') && value.length <= 9 ? value : '#000000';
  return <div className="flex items-center gap-2" data-id="element-1387">
      <input type="color" value={hexValue} onChange={e => onChange(e.target.value)} className="h-8 w-8 rounded border border-gray-300 cursor-pointer flex-shrink-0" data-id="element-1388" />
      <input type="text" value={value} onChange={e => onChange(e.target.value)} className="prop-input flex-1 font-mono text-xs" data-id="element-1389" />
    </div>;
}
function ToggleField({
  label,
  value,
  onChange
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return <div className="flex items-center justify-between" data-id="element-1390">
      <span className="text-xs font-medium text-gray-700" data-id="element-1391">{label}</span>
      <button onClick={() => onChange(!value)} className={`relative w-9 h-5 rounded-full transition-colors ${value ? 'bg-blue-600' : 'bg-gray-300'}`} data-id="element-1392">
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${value ? 'translate-x-4' : ''}`} data-id="element-1393" />
      </button>
    </div>;
}
// ── Columns Canvas with Drop Zones ──
function FormColumnsCanvas({
  block,
  theme,
  selectedChild,
  dragOverCol,
  onColumnDragOver,
  onColumnDrop,
  onSelectChild,
  onDeleteChild
}: {
  block: FormBlock;
  theme: FormTheme;
  selectedChild: {
    parentId: string;
    colIndex: number;
    childId: string;
  } | null;
  dragOverCol: {
    parentId: string;
    colIndex: number;
    position: number;
  } | null;
  onColumnDragOver: (parentId: string, colIndex: number) => (e: React.DragEvent) => void;
  onColumnDrop: (parentId: string, colIndex: number) => (e: React.DragEvent) => void;
  onSelectChild: (colIndex: number, childId: string) => void;
  onDeleteChild: (colIndex: number, childId: string) => void;
}) {
  const colCount = block.columnCount || 2;
  const gap = block.columnGap || 16;
  const cols = block.columns || Array(colCount).fill([]);
  const isOver = (colIdx: number) => dragOverCol?.parentId === block.id && dragOverCol?.colIndex === colIdx;
  return <div style={{
    display: 'grid',
    gridTemplateColumns: `repeat(${colCount}, 1fr)`,
    gap: `${gap}px`
  }} data-id="element-1394">
      {Array.from({
      length: colCount
    }).map((_, colIdx) => {
      const colBlocks = cols[colIdx] || [];
      return <div key={colIdx} onDragOver={onColumnDragOver(block.id, colIdx)} onDragLeave={e => e.stopPropagation()} onDrop={onColumnDrop(block.id, colIdx)} onClick={e => e.stopPropagation()} style={{
        border: isOver(colIdx) ? '2px dashed #3b82f6' : '1px dashed #d1d5db',
        backgroundColor: isOver(colIdx) ? '#eff6ff' : '#f9fafb',
        minHeight: 80,
        padding: 8,
        borderRadius: 6,
        transition: 'border-color 0.15s, background-color 0.15s',
        display: 'flex',
        flexDirection: 'column',
        gap: 8
      }} data-id="element-1395">
            {colBlocks.length === 0 ? <div className="flex-1 flex items-center justify-center" data-id="element-1396">
                <p className="text-xs text-gray-400 text-center" data-id="element-1397">
                  {isOver(colIdx) ? '⬇ Drop here' : `Column ${colIdx + 1}`}
                </p>
              </div> : colBlocks.map(cb => <div key={cb.id} onClick={e => {
          e.stopPropagation();
          onSelectChild(colIdx, cb.id);
        }} className="relative group/child bg-white rounded-md shadow-sm" style={{
          border: selectedChild?.parentId === block.id && selectedChild?.childId === cb.id ? '2px solid #3b82f6' : '1px solid #e5e7eb',
          cursor: 'pointer',
          padding: '8px 12px'
        }} data-id="element-1398">
                  <div className="absolute -right-2 -top-2 opacity-0 group-hover/child:opacity-100 transition-opacity z-10" data-id="element-1399">
                    <button onClick={e => {
              e.stopPropagation();
              onDeleteChild(colIdx, cb.id);
            }} className="p-1 bg-white border border-gray-200 rounded shadow-sm hover:bg-red-50" title="Remove from column" data-id="element-1400">
                      <XIcon className="h-3 w-3 text-red-500" data-id="element-1401" />
                    </button>
                  </div>
                  <div className="pointer-events-none" data-id="element-1402">
                    <BlockPreview block={cb} theme={theme} data-id="element-1403" />
                  </div>
                </div>)}
          </div>;
    })}
    </div>;
}
// ── Block Preview ──
function BlockPreview({
  block,
  theme
}: {
  block: FormBlock;
  theme: FormTheme;
}) {
  switch (block.type) {
    case 'heading':
      const Tag = `h${block.level || 2}` as keyof JSX.IntrinsicElements;
      return <Tag className="font-semibold" style={{
        fontSize: block.level === 1 ? 24 : block.level === 3 ? 16 : 20,
        color: theme.headingColor
      }} data-id="element-1404">
          {block.content || 'Heading'}
        </Tag>;
    case 'paragraph':
      return <p className="text-sm" style={{
        color: theme.paragraphColor
      }} data-id="element-1405">
          {block.content || 'Paragraph text'}
        </p>;
    case 'divider':
      return <hr className="border-gray-200" data-id="element-1406" />;
    case 'spacer':
      return <div className="flex items-center justify-center text-xs text-gray-400" style={{
        height: block.height || 24
      }} data-id="element-1407">
          ↕ {block.height || 24}px
        </div>;
    case 'video_embed':
      {
        if (!block.videoUrl) {
          return <div className="bg-gray-100 border-2 border-dashed border-gray-300 rounded p-6 text-center text-sm text-gray-400" data-id="element-1408">
            Video / GIF — set URL in properties
          </div>;
        }
        const isMp4 = block.videoUrl.toLowerCase().endsWith('.mp4');
        const isGif = block.videoUrl.toLowerCase().endsWith('.gif');
        const isYoutube = block.videoUrl.includes('youtube.com') || block.videoUrl.includes('youtu.be');
        const isVimeo = block.videoUrl.includes('vimeo.com');
        if (isGif) {
          return <img src={block.videoUrl} alt="GIF" className="w-full h-auto rounded-md" data-id="element-1409" />;
        }
        if (isMp4) {
          return <video src={block.videoUrl} controls autoPlay={block.videoAutoplay} muted={block.videoMuted} loop={block.videoLoop} className="w-full h-auto rounded-md" data-id="element-1410" />;
        }
        // YouTube / Vimeo iframe
        let embedUrl = block.videoUrl;
        if (isYoutube) {
          const videoId = block.videoUrl.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?]+)/)?.[1];
          if (videoId) {
            embedUrl = `https://www.youtube.com/embed/${videoId}?rel=0${block.videoAutoplay ? '&autoplay=1' : ''}${block.videoMuted ? '&mute=1' : ''}${block.videoLoop ? `&loop=1&playlist=${videoId}` : ''}`;
          }
        } else if (isVimeo) {
          const videoId = block.videoUrl.match(/vimeo\.com\/(?:video\/)?([0-9]+)/)?.[1];
          if (videoId) {
            embedUrl = `https://player.vimeo.com/video/${videoId}?title=0&byline=0&portrait=0${block.videoAutoplay ? '&autoplay=1' : ''}${block.videoMuted ? '&muted=1' : ''}${block.videoLoop ? '&loop=1' : ''}`;
          }
        }
        const ratioClass = block.videoAspectRatio === '4:3' ? 'aspect-[4/3]' : block.videoAspectRatio === '1:1' ? 'aspect-square' : block.videoAspectRatio === '9:16' ? 'aspect-[9/16]' : 'aspect-video'; // 16:9 default
        return <div className={`w-full relative ${ratioClass} bg-gray-100 rounded-md overflow-hidden`} data-id="element-1411">
          <iframe src={embedUrl} className="absolute top-0 left-0 w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-1412" />
        </div>;
      }
    case 'recaptcha':
      return <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-md max-w-[300px]" data-id="element-1413">
          <div className="flex items-center gap-3" data-id="element-1414">
            <div className="w-6 h-6 rounded-sm border-2 border-gray-300 bg-white" data-id="element-1415"></div>
            <span className="text-sm font-medium text-gray-700" data-id="element-1416">
              I'm not a robot
            </span>
          </div>
          <div className="flex flex-col items-center" data-id="element-1417">
            <ShieldCheckIcon className="h-6 w-6 text-blue-500 mb-1" data-id="element-1418" />
            <span className="text-[10px] text-gray-500" data-id="element-1419">reCAPTCHA</span>
          </div>
        </div>;
    case 'image':
      {
        if (!block.src) {
          return <div className="bg-gray-100 border-2 border-dashed border-gray-300 rounded p-6 text-center text-sm text-gray-400" data-id="element-1420">
            Image — set URL in properties
          </div>;
        }
        const sizing = block.imageSizing || 'maxWidth';
        const imgStyle: React.CSSProperties = {
          display: 'block'
        };
        if (sizing === 'natural') {
          // No constraints — natural dimensions
        } else if (sizing === 'maxWidth') {
          imgStyle.maxWidth = `${block.imageMaxWidth || 100}%`;
          imgStyle.height = 'auto';
        } else if (sizing === 'custom') {
          if (block.imageWidth) imgStyle.width = `${block.imageWidth}px`;
          if (block.imageHeight) imgStyle.height = `${block.imageHeight}px`;
          if (block.imageMaxWidth) imgStyle.maxWidth = `${block.imageMaxWidth}%`;
        }
        if (block.imageMaxHeight) imgStyle.maxHeight = `${block.imageMaxHeight}px`;
        if (block.imageObjectFit) imgStyle.objectFit = block.imageObjectFit as any;
        if (block.imageBorderRadius) imgStyle.borderRadius = `${block.imageBorderRadius}px`;
        if (block.imageOpacity != null && block.imageOpacity < 100) imgStyle.opacity = block.imageOpacity / 100;
        if (block.imageShadow === 'sm') imgStyle.boxShadow = '0 1px 2px 0 rgba(0,0,0,0.05)';else if (block.imageShadow === 'md') imgStyle.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.1)';else if (block.imageShadow === 'lg') imgStyle.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.1)';
        // Alignment margin
        const imgAlign = block.imageAlign || 'center';
        if (imgAlign === 'center') imgStyle.margin = '0 auto';else if (imgAlign === 'right') imgStyle.marginLeft = 'auto';
        // Wrapper styles for position
        const wrapStyle: React.CSSProperties = {
          textAlign: imgAlign
        };
        if (block.imagePosition && block.imagePosition !== 'static') {
          wrapStyle.position = block.imagePosition as any;
          if (block.imageTopOffset != null) wrapStyle.top = `${block.imageTopOffset}px`;
          if (block.imageLeftOffset != null) wrapStyle.left = `${block.imageLeftOffset}px`;
          if (block.imagePosition === 'sticky') wrapStyle.zIndex = 10;
          if (block.imagePosition === 'fixed') wrapStyle.zIndex = 1000;
          if (block.imagePosition === 'absolute') wrapStyle.zIndex = 10;
        }
        return <div style={wrapStyle} data-id="element-1421">
          <img src={block.src} alt={block.alt || ''} style={imgStyle} data-id="element-1422" />
        </div>;
      }
    case 'submit':
      return <div style={{
        textAlign: block.buttonAlign === 'center' ? 'center' : block.buttonAlign === 'right' ? 'right' : 'left'
      }} data-id="element-1423">
          <button className={`px-5 py-2.5 text-sm font-semibold rounded-md ${block.buttonAlign === 'full' ? 'w-full' : ''}`} style={{
          backgroundColor: theme.buttonStyle === 'outline' ? 'transparent' : block.buttonColor || theme.primaryColor,
          color: theme.buttonStyle === 'outline' ? block.buttonColor || theme.primaryColor : theme.buttonTextColor,
          border: theme.buttonStyle === 'outline' ? `2px solid ${block.buttonColor || theme.primaryColor}` : 'none',
          borderRadius: theme.borderRadius,
          boxShadow: theme.buttonShadow ? '0 4px 14px 0 rgba(0,0,0,0.15)' : 'none'
        }} data-id="element-1424">
            {block.buttonText || 'Submit'}
          </button>
        </div>;
    case 'consent':
      return <div data-id="element-1425">
          <label className="flex items-start gap-2 cursor-pointer" data-id="element-1426">
            <input type="checkbox" disabled className="mt-0.5" style={{
            accentColor: theme.primaryColor
          }} data-id="element-1427" />
            <span className="text-sm" style={{
            color: theme.labelColor,
            lineHeight: 1.4
          }} data-id="element-1428">
              {block.consentText || 'I agree to receive updates'}
              {block.required && <span className="text-red-500 ml-0.5" data-id="element-1429">*</span>}
            </span>
          </label>
          {block.helpText && <p className="text-xs text-gray-400 mt-1 ml-6" data-id="element-1430">{block.helpText}</p>}
        </div>;
    case 'hidden':
      return <div className="text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded font-mono" data-id="element-1431">
          hidden: {block.name} = {block.defaultValue || '(empty)'}
        </div>;
    case 'section':
      {
        const showTitle = block.sectionShowTitle !== false;
        const anim = block.sectionAnimation && block.sectionAnimation !== 'none' ? block.sectionAnimation : null;
        const borderClass = block.sectionBorderStyle === 'dashed' ? 'border-dashed' : block.sectionBorderStyle === 'accent' ? 'border-l-4 border-l-blue-500' : block.sectionBorderStyle === 'none' ? 'border-transparent' : '';
        return <div className={`rounded-lg border border-gray-200 overflow-hidden ${borderClass}`} style={{
          background: block.sectionBackground || undefined
        }} data-id="element-1432">
          {showTitle && <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200" data-id="element-1433">
              <LayoutListIcon className="h-4 w-4 text-blue-500 flex-shrink-0" data-id="element-1434" />
              <span className="text-sm font-semibold flex-1" style={{
              color: theme.headingColor
            }} data-id="element-1435">
                {block.sectionTitle || 'Section'}
              </span>
              {anim && <span className="text-[9px] px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded-full font-medium" data-id="element-1436">
                  {anim}
                </span>}
            </div>}
          {!showTitle && <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50/50 border-b border-gray-100" data-id="element-1437">
              <LayoutListIcon className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" data-id="element-1438" />
              <span className="text-[10px] text-gray-400 italic flex-1" data-id="element-1439">
                {block.sectionTitle || 'Section'} (title hidden)
              </span>
              {anim && <span className="text-[9px] px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded-full font-medium" data-id="element-1440">
                  {anim}
                </span>}
            </div>}
          <div className="p-3" data-id="element-1441">
            {block.children && block.children.length > 0 ? <div className="space-y-2" data-id="element-1442">
                {block.children.map(child => <div key={child.id} className="text-xs text-gray-600 bg-white rounded-md px-3 py-2 border border-gray-200 flex items-center gap-2 group/child" data-id="element-1443">
                    <GripVerticalIcon className="h-3 w-3 text-gray-300 flex-shrink-0" data-id="element-1444" />
                    <span className="flex-1" data-id="element-1445">{child.label || child.type}</span>
                    <span className="text-[9px] text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded" data-id="element-1446">
                      {child.type}
                    </span>
                  </div>)}
              </div> : null}
            <div className="border-2 border-dashed border-gray-200 rounded-md p-3 text-center mt-2 hover:border-blue-300 hover:bg-blue-50/30 transition-colors" data-id="element-1447">
              <p className="text-[10px] text-gray-400" data-id="element-1448">Drop fields here</p>
            </div>
          </div>
        </div>;
      }
    case 'subform':
      {
        const columns = block.subformColumns || [{
          label: 'Column 1',
          type: 'text'
        }, {
          label: 'Column 2',
          type: 'text'
        }];
        const showName = block.subformShowName !== false;
        const minR = block.subformMinRows || 1;
        const maxR = block.subformMaxRows || 10;
        return <div className="rounded-lg border border-gray-200 overflow-hidden bg-white" data-id="element-1449">
          {showName ? <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200" data-id="element-1450">
              <TableIcon className="h-4 w-4 text-blue-500 flex-shrink-0" data-id="element-1451" />
              <span className="text-sm font-semibold flex-1" style={{
              color: theme.headingColor
            }} data-id="element-1452">
                {block.subformName || 'Subform'}
              </span>
              <span className="text-[9px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-1453">
                {minR}–{maxR} rows
              </span>
            </div> : <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50/50 border-b border-gray-100" data-id="element-1454">
              <TableIcon className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" data-id="element-1455" />
              <span className="text-[10px] text-gray-400 italic flex-1" data-id="element-1456">
                {block.subformName || 'Subform'} (name hidden)
              </span>
              <span className="text-[9px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-1457">
                {minR}–{maxR} rows
              </span>
            </div>}
          <div className="overflow-x-auto" data-id="element-1458">
            <table className="w-full text-left border-collapse" data-id="element-1459">
              <thead data-id="element-1460">
                <tr className="border-b border-gray-200 bg-gray-50/50" data-id="element-1461">
                  <th className="px-3 py-2 text-xs font-medium text-gray-500 w-8" data-id="element-1462">
                    #
                  </th>
                  {columns.map((col, i) => <th key={i} className="px-3 py-2 text-xs font-medium text-gray-500" data-id="element-1463">
                      {col.label}
                      {col.required && <span className="text-red-500 ml-0.5" data-id="element-1464">*</span>}
                    </th>)}
                  <th className="px-3 py-2 w-8" data-id="element-1465"></th>
                </tr>
              </thead>
              <tbody data-id="element-1466">
                <tr className="border-b border-gray-100" data-id="element-1467">
                  <td className="px-3 py-1.5 text-gray-400" data-id="element-1468">1</td>
                  {columns.map((col, i) => <td key={i} className="px-3 py-1.5" data-id="element-1469">
                      <div className="w-full px-2 py-1 rounded text-gray-400 text-xs" style={{
                      border: `1px solid ${theme.inputBorderColor}`,
                      background: theme.inputBackground,
                      borderRadius: theme.borderRadius
                    }} data-id="element-1470">
                        {col.placeholder || col.type}
                      </div>
                    </td>)}
                  <td className="px-3 py-1.5" data-id="element-1471">
                    <Trash2Icon className="h-3 w-3 text-gray-300" data-id="element-1472" />
                  </td>
                </tr>
                <tr className="border-b border-gray-100" data-id="element-1473">
                  <td className="px-3 py-1.5 text-gray-400" data-id="element-1474">2</td>
                  {columns.map((_, i) => <td key={i} className="px-3 py-1.5" data-id="element-1475">
                      <div className="w-full px-2 py-1 rounded text-gray-300" style={{
                      border: `1px dashed ${theme.inputBorderColor}`,
                      background: theme.inputBackground,
                      borderRadius: theme.borderRadius
                    }} data-id="element-1476">
                        &nbsp;
                      </div>
                    </td>)}
                  <td className="px-3 py-1.5" data-id="element-1477">
                    <Trash2Icon className="h-3 w-3 text-gray-300" data-id="element-1478" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100" data-id="element-1479">
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-400 cursor-default" data-id="element-1480">
              <PlusIcon className="h-3 w-3" data-id="element-1481" /> Add Row
            </span>
            <span className="text-[9px] text-gray-400" data-id="element-1482">
              {columns.length} field{columns.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>;
      }
    case 'phone':
      {
        const defaultCountry = block.phoneDefaultCountry || 'US';
        const country = COUNTRY_CODES.find(c => c.iso === defaultCountry) || COUNTRY_CODES.find(c => c.iso === 'US')!;
        return <div data-id="element-1483">
          <div className="flex items-center gap-1 mb-1" data-id="element-1484">
            <label className="block" style={{
              fontSize: theme.labelFontSize,
              fontWeight: Number(theme.labelFontWeight),
              color: theme.labelColor
            }} data-id="element-1485">
              {block.label}
              {block.required && <span className="text-red-500 ml-0.5" data-id="element-1486">*</span>}
            </label>
            {block.width === 'half' && <span className="text-[10px] px-1 py-0.5 bg-blue-50 text-blue-600 rounded font-medium" data-id="element-1487">
                ½
              </span>}
          </div>
          <div className="w-full flex items-stretch text-sm text-gray-400 overflow-hidden" style={{
            borderRadius: theme.borderRadius,
            border: `1px solid ${theme.inputBorderColor}`,
            background: theme.inputBackground,
            boxShadow: theme.inputShadow ? '0 1px 3px 0 rgba(0,0,0,0.08)' : 'none'
          }} data-id="element-1488">
            <div className="flex items-center gap-1.5 px-3 bg-gray-50/50 border-r" style={{
              borderColor: theme.inputBorderColor
            }} data-id="element-1489">
              <span data-id="element-1490">{country.flag}</span>
              <span className="text-gray-600 font-medium" data-id="element-1491">
                {country.dialCode}
              </span>
              <ChevronDownIcon className="h-3 w-3 text-gray-400 ml-1" data-id="element-1492" />
            </div>
            <div className="flex-1 px-3 py-2 flex items-center" data-id="element-1493">
              {block.placeholder || '(555) 000-0000'}
            </div>
          </div>
          {block.helpText && <p className="text-xs text-gray-400 mt-1" data-id="element-1494">{block.helpText}</p>}
        </div>;
      }
    default:
      return <div data-id="element-1495">
          <div className="flex items-center gap-1 mb-1" data-id="element-1496">
            <label className="block" style={{
            fontSize: theme.labelFontSize,
            fontWeight: Number(theme.labelFontWeight),
            color: theme.labelColor
          }} data-id="element-1497">
              {block.label}
              {block.required && <span className="text-red-500 ml-0.5" data-id="element-1498">*</span>}
            </label>
            {block.width === 'half' && <span className="text-[10px] px-1 py-0.5 bg-blue-50 text-blue-600 rounded font-medium" data-id="element-1499">
                ½
              </span>}
          </div>
          {block.type === 'textarea' ? <div className="w-full h-20 px-3 py-2 text-sm text-gray-400" style={{
          borderRadius: theme.borderRadius,
          border: `1px solid ${theme.inputBorderColor}`,
          background: theme.inputBackground,
          boxShadow: theme.inputShadow ? '0 1px 3px 0 rgba(0,0,0,0.08)' : 'none'
        }} data-id="element-1500">
              {block.placeholder}
            </div> : block.type === 'select' ? <div className="w-full px-3 py-2 text-sm text-gray-400" style={{
          borderRadius: theme.borderRadius,
          border: `1px solid ${theme.inputBorderColor}`,
          background: theme.inputBackground
        }} data-id="element-1501">
              {block.placeholder || 'Select...'}
            </div> : block.type === 'radio' || block.type === 'checkbox' ? <div className={`mt-1 ${block.optionsLayout === 'horizontal' ? 'flex flex-wrap gap-x-4 gap-y-1' : block.optionsColumns && block.optionsColumns > 1 ? `grid gap-x-4 gap-y-1` : 'space-y-1'}`} style={block.optionsColumns && block.optionsColumns > 1 && block.optionsLayout !== 'horizontal' ? {
          gridTemplateColumns: `repeat(${block.optionsColumns}, minmax(0, 1fr))`
        } : undefined} data-id="element-1502">
              {(block.validation.options || []).map((opt, i) => <label key={i} className="flex items-center gap-2 text-sm text-gray-600" data-id="element-1503">
                  <input type={block.type === 'radio' ? 'radio' : 'checkbox'} disabled className="accent-blue-600" style={{
              accentColor: theme.primaryColor
            }} data-id="element-1504" />
                  {opt.label}
                </label>)}
            </div> : <div className="w-full px-3 py-2 text-sm text-gray-400" style={{
          borderRadius: theme.borderRadius,
          border: `1px solid ${theme.inputBorderColor}`,
          background: theme.inputBackground,
          boxShadow: theme.inputShadow ? '0 1px 3px 0 rgba(0,0,0,0.08)' : 'none'
        }} data-id="element-1505">
              {block.placeholder || block.type}
            </div>}
          {block.helpText && <p className="text-xs text-gray-400 mt-1" data-id="element-1506">{block.helpText}</p>}
        </div>;
  }
}